import { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import { formatMoney, formatDate, currencySymbol, CURRENCIES } from '../../utils/format';
import Tag from '../shared/Tag';
import CopyButton from '../shared/CopyButton';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import { useApp } from '../../context';
import { isAdmin, canAccess } from '../../utils/rbac';
import SowSelect from '../shared/SowSelect';
import StatusFilter from '../shared/StatusFilter';
import { useToast } from '../ui/Toast';
import { totalUnits, defaultInventoryMode, needsIndividualConfirm, linesMissingStock } from '../../utils/inventoryMode';
import { poSowSpread } from '../../utils/poSowSpread';
import MissingStockPicker from './MissingStockPicker';
import ProductSupplyNote from './ProductSupplyNote';

const PO_STATUSES = ['draft', 'issued', 'acknowledged', 'part_delivered', 'delivered', 'closed', 'cancelled'];
const INACTIVE_STATUSES = new Set(['closed', 'cancelled']);
// 'draft' included so creation attributes (order date, delivery address, terms,
// requester, quote ref…) can be corrected before issue — the backend only records
// a revision for edits to a NON-draft PO, so draft edits are free/untracked.
const EDITABLE_STATUSES = new Set(['draft', 'issued', 'acknowledged', 'part_delivered', 'delivered']);
const PAGE_SIZE = 25;

// Valid next statuses for each current status — mirrors the backend rule
const VALID_TRANSITIONS = {
  draft:          ['issued', 'cancelled'],
  issued:         ['acknowledged', 'cancelled'],
  acknowledged:   ['part_delivered', 'delivered', 'cancelled'],
  part_delivered: ['delivered', 'closed'],
  delivered:      ['closed'],
  closed:         [],
  cancelled:      [],
};

const STATUS_LABELS = {
  draft:          'Draft',
  issued:         'Issued',
  acknowledged:   'Acknowledged',
  part_delivered: 'Part Delivered',
  delivered:      'Delivered',
  closed:         'Closed',
  cancelled:      'Cancelled',
};

const STATUS_COLORS = {
  draft:          'bg-gray-100 text-gray-600',
  issued:         'bg-blue-100 text-blue-700',
  acknowledged:   'bg-sky-100 text-sky-700',
  part_delivered: 'bg-amber-100 text-amber-700',
  delivered:      'bg-emerald-100 text-emerald-700',
  closed:         'bg-navy/10 text-navy',
  cancelled:      'bg-red-100 text-red-600',
};

function StatusBadge({ status }) {
  return (
    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function esc(str) {
  if (str == null) return '';
  const d = document.createElement('div');
  d.textContent = String(str);
  return d.innerHTML;
}

function generatePOHtml(po) {
  // The PO prints in the currency it was raised in. For anything other than
  // sterling the code is spelled out next to the total and in the invoice
  // instructions, so the supplier can't read a € figure as £.
  const cur = (po.currency || 'GBP').toUpperCase();
  const sym = currencySymbol(cur) || cur;
  const money = (v) => `${sym} ${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const lines = (po.lines || []).map((l, i) => `
    <tr>
      <td class="lnum">${i + 1}</td>
      <td class="ldesc">
        <div class="ldesc-name">${esc(l.description)}</div>
        ${l.part_number ? `<div class="lpart">${esc(l.part_number)}</div>` : ''}
      </td>
      <td class="lqty">${Number(l.quantity || 0)}</td>
      <td class="lprice">${money(l.unit_price)}</td>
      <td class="ltotal">${money(l.line_total)}</td>
    </tr>`).join('');

  // SHIP TO shows the actual delivery address only — never the internal
  // address label (e.g. "ATV - Pride Park"), which read awkwardly on a PO sent
  // to a supplier.
  const deliveryAddr = [
    po.delivery_company_name,
    po.address_line1,
    po.address_line2,
    po.address_line3,
    po.city,
    po.county,
    po.country || 'United Kingdom',
    po.postcode,
  ].filter(Boolean).map(esc).join('\n');

  // Show the delivery contact as "FAO: <name>" — a per-PO attention name wins,
  // else the address's stored contact. Don't double up an existing "FAO" prefix.
  const rawContact = (po.delivery_attention || po.delivery_contact || '').trim();
  const deliveryContact = rawContact
    ? (/^fao\b[:\s]*/i.test(rawContact) ? esc(rawContact) : `FAO: ${esc(rawContact)}`)
    : '';

  const orderDateFmt = po.order_date
    ? new Date(po.order_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<title>PO ${po.po_number}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; background: #fff; padding: 30px 40px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
  .logo-block { display: flex; gap: 14px; align-items: flex-start; }
  .atv-logo { width: 60px; height: auto; flex-shrink: 0; }
  .company-addr { font-size: 10px; line-height: 1.6; }
  .company-name { font-weight: bold; font-size: 12px; }
  .po-title-block { text-align: right; }
  .po-title { font-size: 22px; font-weight: bold; letter-spacing: 1px; margin-bottom: 6px; }
  .po-meta { font-size: 11px; line-height: 1.8; }
  .po-meta strong { font-size: 12px; }
  .address-row { display: flex; gap: 0; margin: 18px 0; border: 1px solid #888; }
  .addr-col { flex: 1; padding: 12px 14px; font-size: 10px; line-height: 1.7; }
  .addr-col:first-child { border-right: 1px solid #888; }
  .addr-label { font-weight: bold; font-size: 11px; margin-bottom: 4px; }
  .vendor-block { margin: 14px 0 18px; font-size: 11px; line-height: 1.7; }
  .vendor-label { font-weight: bold; font-size: 13px; }
  table.lines { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  table.lines thead tr { background: #f0f0f0; }
  table.lines th { padding: 5px 8px; font-size: 9px; font-weight: bold; text-transform: uppercase; text-align: left; border-bottom: 1px solid #999; }
  table.lines th.r, table.lines td.lprice, table.lines td.ltotal { text-align: right; }
  table.lines th.c, table.lines td.lqty { text-align: center; }
  table.lines td { padding: 8px 8px; vertical-align: top; border-bottom: 1px dashed #ccc; }
  .lnum { width: 28px; font-weight: bold; }
  .lpart { font-size: 9px; color: #555; }
  .total-row { display: flex; justify-content: flex-end; margin-bottom: 24px; }
  .total-box { display: flex; align-items: center; gap: 20px; border-top: 2px solid #222; padding-top: 8px; }
  .total-label { font-weight: bold; font-size: 12px; }
  .total-val { font-weight: bold; font-size: 14px; }
  .section { border-top: 1px solid #888; margin-top: 16px; padding-top: 10px; }
  .section-label { font-size: 10px; color: #555; font-weight: bold; margin-bottom: 6px; }
  .section-body { font-family: 'Courier New', Courier, monospace; font-size: 10px; color: #444; line-height: 1.8; }
  .footer { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 8px; font-size: 8.5px; color: #555; text-align: center; line-height: 1.6; }
  @media print {
    body { padding: 20px; }
    /* Suppress browser-generated print header/footer (URL, page number, date) */
    @page { size: A4; margin: 0mm; }
    body { margin: 15mm; }
  }
</style>
</head>
<body>
  <div class="header">
    <div class="logo-block">
      <svg class="atv-logo" xmlns="http://www.w3.org/2000/svg" viewBox="96 120 188 152">
        <path d="M0 0 C3.3270875 1.30507377 5.75569832 3.80258576 8.38037109 6.16162109 C13.09371843 10.30206516 17.85619552 14.3218713 22.81787109 18.16162109 C28.95491726 22.92294171 34.95801015 27.83317262 40.94311523 32.78369141 C46.25366272 37.1746715 51.60672911 41.50423967 57.00537109 45.78662109 C57.83552734 46.53685547 58.66568359 47.28708984 59.52099609 48.06005859 C61.76742929 49.96558698 61.76742929 49.96558698 63.95068359 49.81005859 C66.89547112 48.34326328 69.29922051 46.45208178 71.88037109 44.41162109 C72.43974365 43.97333984 72.99911621 43.53505859 73.57543945 43.08349609 C75.39124926 41.6584238 77.1991429 40.2238145 79.00537109 38.78662109 C80.9099167 37.27253902 82.81496239 35.75913756 84.72265625 34.24902344 C85.9298386 33.2922859 87.13407277 32.33181362 88.33496094 31.3671875 C91.12466173 29.15023368 93.79113911 27.31040515 97.00537109 25.78662109 C97.25997057 33.0266565 97.43446737 40.26552975 97.5546875 47.5090332 C97.60482391 49.97008393 97.67295452 52.43083903 97.75976562 54.89086914 C98.50160377 76.46090427 98.50160377 76.46090427 94.96557617 81.27270508 C92.42570063 83.57787265 90.02369721 85.17784424 87.00537109 86.78662109 C85.83824803 87.69242738 84.68777112 88.62056913 83.56396484 89.57958984 C82.53400391 90.36978516 81.50404297 91.15998047 80.44287109 91.97412109 C75.04826044 96.14923736 69.75674651 100.43286106 64.50537109 104.78662109 C58.70916303 109.58775798 52.85829197 114.30274527 46.92285156 118.93041992 C41.54443344 123.12842095 36.26444734 127.43983358 31.00537109 131.78662109 C27.64280212 130.28271983 25.07507668 128.33409207 22.25537109 125.97412109 C21.28728516 125.17103516 20.31919922 124.36794922 19.32177734 123.54052734 C18.79213379 123.0998291 18.26249023 122.65913086 17.71679688 122.20507812 C14.5584073 119.58735438 11.37564169 116.9996026 8.19287109 114.41162109 C7.17084595 113.57981323 7.17084595 113.57981323 6.12817383 112.73120117 C1.72836885 109.15931369 -2.71137974 105.644106 -7.18212891 102.16162109 C-12.96776495 97.65261614 -18.58420377 92.96795642 -24.15478516 88.19677734 C-25.56050781 87.00375 -25.56050781 87.00375 -26.99462891 85.78662109 C-27.83896484 85.00158203 -28.68330078 84.21654297 -29.55322266 83.40771484 C-31.84104742 81.57544296 -31.84104742 81.57544296 -34.40478516 81.73193359 C-37.88518338 83.14929034 -40.56722293 85.28801515 -43.43212891 87.66162109 C-44.66641284 88.65652574 -45.90079075 89.65131383 -47.13525391 90.64599609 C-48.08368164 91.4184668 -48.08368164 91.4184668 -49.05126953 92.20654297 C-52.2414523 94.80036996 -55.45941436 97.35841265 -58.68212891 99.91162109 C-59.26986084 100.3845459 -59.85759277 100.8574707 -60.46313477 101.34472656 C-62.97142379 103.33652757 -64.92864741 104.76462726 -67.99462891 105.78662109 C-68.35209748 98.60227863 -68.59574174 91.42025227 -68.76367188 84.22900391 C-68.83371838 81.7880285 -68.9289983 79.34763305 -69.05078125 76.90869141 C-70.09048321 55.53801076 -70.09048321 55.53801076 -65.05541992 49.62597656 C-62.23128983 46.96612866 -59.18343901 44.86899316 -55.84156799 42.9135437 C-52.74274261 41.02277405 -49.99203397 38.70055751 -47.18212891 36.41162109 C-46.02012092 35.5070079 -44.85754971 34.60311648 -43.69384766 33.70068359 C-39.56460682 30.47182032 -35.53023026 27.1312724 -31.49462891 23.78662109 C-25.60762424 18.90860818 -19.66826086 14.11358687 -13.62207031 9.43408203 C-10.58749143 7.07752651 -7.69118567 4.64297438 -4.86962891 2.03662109 C-1.99462891 -0.21337891 -1.99462891 -0.21337891 0 0 Z" fill="#E4B77A" transform="translate(172.99462890625,122.21337890625)"/>
        <path d="M0 0 C3.3270875 1.30507377 5.75569832 3.80258576 8.38037109 6.16162109 C13.09371843 10.30206516 17.85619552 14.3218713 22.81787109 18.16162109 C28.95572628 22.92356937 34.95992199 27.83409569 40.9453125 32.78588867 C45.5874181 36.6248194 50.26231481 40.41714046 54.97802734 44.16552734 C57.68828491 46.33268898 60.35107426 48.55142376 63.00537109 50.78662109 C60.104757 54.71424688 57.03825025 57.49776606 53.13037109 60.41162109 C51.97567199 61.27349302 50.82206601 62.13683097 49.66943359 63.00146484 C49.07163086 63.44989746 48.47382813 63.89833008 47.85791016 64.36035156 C45.939718 65.8371674 44.07582213 67.36561372 42.21630859 68.91552734 C41.5961084 69.42583496 40.9759082 69.93614258 40.33691406 70.46191406 C39.13619462 71.45328386 37.94466612 72.45591553 36.76367188 73.47070312 C34.29843654 75.48324344 32.67467603 76.76729466 29.43505859 76.86083984 C26.31501238 75.48139819 24.1833742 73.67857343 21.63037109 71.41162109 C16.83956451 67.24616996 11.89296311 63.39105084 6.75537109 59.66162109 C6.08715332 59.1764502 5.41893555 58.6912793 4.73046875 58.19140625 C3.15772052 57.05331443 1.58189429 55.91947792 0.00537109 54.78662109 C-3.61170297 56.39801902 -6.50434161 58.60557003 -9.61962891 61.03662109 C-10.17448975 61.46845703 -10.72935059 61.90029297 -11.30102539 62.34521484 C-12.52949303 63.30160126 -13.75689711 64.25935493 -14.98339844 65.21826172 C-17.12602342 66.88908266 -19.27643828 68.54949031 -21.42822266 70.20849609 C-29.11199984 76.14229241 -36.69715186 82.14474895 -44.11572266 88.40771484 C-48.31650261 91.87891565 -52.64481011 95.17053041 -56.98681641 98.46240234 C-59.13843662 100.12501796 -61.19635882 101.80807108 -63.25244141 103.57958984 C-65.99462891 105.78662109 -65.99462891 105.78662109 -67.99462891 105.78662109 C-68.35209748 98.60227863 -68.59574174 91.42025227 -68.76367188 84.22900391 C-68.83371838 81.7880285 -68.9289983 79.34763305 -69.05078125 76.90869141 C-70.09048321 55.53801076 -70.09048321 55.53801076 -65.05541992 49.62597656 C-62.23128983 46.96612866 -59.18343901 44.86899316 -55.84156799 42.9135437 C-52.74274261 41.02277405 -49.99203397 38.70055751 -47.18212891 36.41162109 C-46.02012092 35.5070079 -44.85754971 34.60311648 -43.69384766 33.70068359 C-39.56460682 30.47182032 -35.53023026 27.1312724 -31.49462891 23.78662109 C-25.60762424 18.90860818 -19.66826086 14.11358687 -13.62207031 9.43408203 C-10.58749143 7.07752651 -7.69118567 4.64297438 -4.86962891 2.03662109 C-1.99462891 -0.21337891 -1.99462891 -0.21337891 0 0 Z" fill="#1B2238" transform="translate(172.99462890625,122.21337890625)"/>
      </svg>
      <div class="company-addr">
        <div class="company-name">Alton Valley</div>
        15 St Christopher's Way<br/>
        Derby<br/>
        Derbyshire<br/>
        United Kingdom DE24 8JY
      </div>
    </div>
    <div class="po-title-block">
      <div class="po-title">PURCHASE ORDER</div>
      <div class="po-meta">
        Order Date: ${esc(orderDateFmt)}<br/>
        <strong>Purchase Order Number: ${esc(po.po_number)}</strong>
      </div>
    </div>
  </div>

  <div class="address-row">
    <div class="addr-col">
      <div class="addr-label">INVOICE TO:</div>
      Accounts Payable<br/>
      Alton Valley Limited<br/>
      15 St Christopher's Way<br/>
      Derby<br/>
      Derbyshire<br/>
      United Kingdom<br/>
      DE24 8JY
    </div>
    <div class="addr-col">
      <div class="addr-label">SHIP TO:</div>
      ${deliveryAddr.replace(/\n/g, '<br/>')}<br/>
      ${deliveryContact}
    </div>
  </div>

  <div class="vendor-block">
    <span class="vendor-label">Vendor: ${esc(po.supplier_name) || '—'}</span><br/>
    ${po.sow_reference ? `SOW: ${esc(po.sow_reference)}<br/>` : ''}
    ${po.quote_reference ? `Quote Reference: ${esc(po.quote_reference)}<br/>` : ''}
    ${po.notes ? esc(po.notes) : ''}
  </div>

  <table class="lines">
    <thead>
      <tr>
        <th style="width:28px">Line</th>
        <th>Product Details</th>
        <th class="c" style="width:48px">Qty</th>
        <th class="r" style="width:90px">Price</th>
        <th class="r" style="width:90px">Total</th>
      </tr>
    </thead>
    <tbody>${lines}</tbody>
  </table>

  <div class="total-row">
    <div class="total-box">
      <div class="total-label">PO Total excluding VAT${cur !== 'GBP' ? ` (${cur})` : ''}</div>
      <div class="total-val">${money(po.subtotal)}</div>
    </div>
  </div>

  <div class="section">
    <div class="section-label">Invoice Details</div>
    <div class="section-body">
      Invoices should be submitted to:&nbsp;&nbsp;&nbsp;accounts@altonvalley.com<br/>
      ${cur !== 'GBP' ? `This order is placed in ${cur}. Please invoice in ${cur}.<br/>` : ''}
      Unless specifically stated, payment terms are net monthly.
    </div>
  </div>

  ${po.specific_terms ? `<div class="section">
    <div class="section-label">Specific PO Terms and Conditions</div>
    <div class="section-body">${esc(po.specific_terms).replace(/\n/g,'<br/>')}</div>
  </div>` : ''}

  <div class="footer">
    Registered in England and Wales No. 07081802, VAT Registration Number GB 980718590<br/>
    Registered Address 15 St Christophers Way, Pride Park, Derby, DE24 8JY
  </div>
</body>
</html>`;
}

export default function POPage() {
  const { detailId, currentUser, navigate } = useApp();
  const { toast } = useToast();
  const [pos, setPOs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [sows, setSows] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState('lines');
  const [listTab, setListTab] = useState('active');   // 'active' | 'inactive'
  // 'all' | one of PO_STATUSES — narrows the open tab to a single status.
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [showRecordInvoice, setShowRecordInvoice] = useState(false);
  const [supplierInvoiceForm, setSupplierInvoiceForm] = useState({ invoice_number: '', invoice_date: '', amount_excl_vat: '', amount_incl_vat: '', payment_date: '', notes: '' });
  const [showManageAddresses, setShowManageAddresses] = useState(false);

  // Search filter
  const [poSearch, setPoSearch] = useState('');
  const [poSort, setPoSort] = useState({ key: 'order_date', dir: 'desc' });

  // Transition modal state
  const [transitionModal, setTransitionModal] = useState(null);  // null | 'cancel' | 'acknowledge' | 'confirm'
  const [transitionTarget, setTransitionTarget] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [expectedDelivDate, setExpectedDelivDate] = useState('');
  const [acknowledgeComment, setAcknowledgeComment] = useState('');
  const [lineDelivDates, setLineDelivDates] = useState({});  // { [line_id]: 'YYYY-MM-DD' }
  const [inventoryMode, setInventoryMode] = useState('individual'); // 'individual' | 'batch' | 'none'
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState('');

  // Edit PO modal state
  const [showEditPO, setShowEditPO] = useState(false);

  // History tab
  const [poHistory, setPoHistory] = useState([]);

  // Inventory items linked to the open PO. The list is capped at 200 rows, so the
  // count is kept separately: a PO whose stock records ran away is exactly the one
  // where the number matters and the page of rows does not.
  const [poInventory, setPoInventory] = useState([]);
  const [poInventoryTotal, setPoInventoryTotal] = useState(0);
  const [consolidating, setConsolidating] = useState(false);

  // Add Line modal
  const [showAddLine, setShowAddLine] = useState(false);
  const [addLineForm, setAddLineForm] = useState({ description: '', part_number: '', quantity: 1, unit_price: '', product_catalog_id: '', line_type: 'stock', service_status: 'outstanding', service_forecast_date: '' });
  const [addLineCatalog, setAddLineCatalog] = useState([]);
  const [addLineCatSearch, setAddLineCatSearch] = useState('');
  const [addLineCatItem, setAddLineCatItem] = useState(null);
  const [catalogSuggestions, setCatalogSuggestions] = useState({ related: [], bundles: [] });
  const setALF = (k, v) => setAddLineForm(p => ({ ...p, [k]: v }));

  // Notes / commentary
  const [poNotes, setPoNotes] = useState([]);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteAttachment, setNoteAttachment] = useState(null);
  const [savingNote, setSavingNote] = useState(false);

  // Quote uploads tab
  const [quoteUploads, setQuoteUploads] = useState([]);
  const [quotesLoaded, setQuotesLoaded] = useState(false);
  const [deletingQuoteId, setDeletingQuoteId] = useState(null);

  // Notify Requester modal — opened when a PO is marked delivered but we can't
  // work out who asked for the goods, so the address has to be typed in.
  const [notifyRequester, setNotifyRequester] = useState(null);
  const [notifyForm, setNotifyForm] = useState({ name: '', email: '' });
  const [notifySending, setNotifySending] = useState(false);
  const [notifyError, setNotifyError] = useState('');

  // Record Delivery modal
  const [showDelivery, setShowDelivery] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoaded, setDeliveriesLoaded] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  // Units received, by record, for records holding more than one. Absent means
  // the whole record arrived.
  const [receivedQty, setReceivedQty] = useState({});
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [submittingDelivery, setSubmittingDelivery] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');
  const [locations, setLocations] = useState([]);
  const [backfillMode, setBackfillMode] = useState('batch');
  const [creatingStock, setCreatingStock] = useState(false);
  const [backfillError, setBackfillError] = useState('');

  // Goods arriving on lines the register has no record of. Marking such a PO
  // delivered asks which lines go into stock and where, and a PO already marked
  // delivered without them can be put right from its header. serverMissingLines
  // holds the server's list when it refuses a transition the screen thought was
  // clear (a stale page, or a record list too long to check here).
  const [stockLineIds, setStockLineIds] = useState(new Set());
  const [stockMode, setStockMode] = useState('batch');
  const [receiveLocation, setReceiveLocation] = useState('');
  const [serverMissingLines, setServerMissingLines] = useState(null);
  const [showAddToStock, setShowAddToStock] = useState(false);
  const [addingToStock, setAddingToStock] = useState(false);
  const [addToStockError, setAddToStockError] = useState('');
  const [editingLineId, setEditingLineId] = useState(null);
  const [editLineForm, setEditLineForm] = useState({});
  const [editLineCatSearch, setEditLineCatSearch] = useState('');
  const [editLineCatItem, setEditLineCatItem] = useState(null);
  const setELF = (k, v) => setEditLineForm(p => ({ ...p, [k]: v }));

  const openEditLine = (l) => {
    setEditingLineId(l.id);
    setEditLineForm({ description: l.description || '', part_number: l.part_number || '', quantity: l.quantity, unit_price: l.unit_price, product_catalog_id: l.product_catalog_id || '' });
    setEditLineCatSearch('');
    setEditLineCatItem(l.product_catalog_id ? { id: l.product_catalog_id, description: l.description, code: l.part_number || '' } : null);
    if (addLineCatalog.length === 0) api.get('product-catalog').then(setAddLineCatalog).catch(() => {});
  };

  const saveEditLine = async () => {
    await api.put(`purchase-orders/${detail.id}/lines/${editingLineId}`, {
      description: editLineForm.description,
      part_number: editLineForm.part_number || null,
      quantity: parseInt(editLineForm.quantity) || 1,
      unit_price: parseFloat(editLineForm.unit_price) || 0,
      product_catalog_id: editLineForm.product_catalog_id || null,
    });
    setEditingLineId(null);
    viewDetail(detail.id);
  };

  const deleteLine = async (lineId) => {
    const issued = detail && detail.status !== 'draft';
    if (!window.confirm(issued
      ? 'Remove this line from an issued PO? This is recorded as a tracked revision and recalculates the PO total.'
      : 'Remove this line?')) return;
    try {
      await api.delete(`purchase-orders/${detail.id}/lines/${lineId}`);
      viewDetail(detail.id);
    } catch (e) {
      alert(e.message || 'Failed to delete line');
    }
  };

  const filteredEditLineCatalog = editLineCatSearch.trim()
    ? addLineCatalog.filter(c =>
        c.description.toLowerCase().includes(editLineCatSearch.toLowerCase()) ||
        c.code.toLowerCase().includes(editLineCatSearch.toLowerCase()) ||
        (c.manufacturer || '').toLowerCase().includes(editLineCatSearch.toLowerCase())
      ).slice(0, 8)
    : [];

  const openAddLine = () => {
    setAddLineForm({ description: '', part_number: '', quantity: 1, unit_price: '', product_catalog_id: '', line_type: 'stock', service_status: 'outstanding', service_forecast_date: '' });
    setAddLineCatSearch('');
    setAddLineCatItem(null);
    api.get('product-catalog').then(setAddLineCatalog).catch(() => setAddLineCatalog([]));
    setShowAddLine(true);
  };

  const closeAddLine = () => { setShowAddLine(false); setAddLineCatSearch(''); setAddLineCatItem(null); setCatalogSuggestions({ related: [], bundles: [] }); };

  const selectCatalogItem = (item) => {
    setAddLineCatItem(item);
    setAddLineCatSearch('');
    setCatalogSuggestions({ related: [], bundles: [] });
    // Pull the cost through: prefer the catalog unit_cost, else the supplier
    // price range (min_price) — most products price via their supplier rows
    // (product_catalog_suppliers), leaving the pc.unit_cost column empty, which
    // is why the line landed at £0. min_price is the cheapest supplier price.
    const catalogCost = item.unit_cost != null ? item.unit_cost
      : (item.min_price != null ? item.min_price : null);
    setAddLineForm(p => ({
      ...p,
      product_catalog_id: item.id,
      description: item.description || '',
      part_number:  item.code || '',
      unit_price:   catalogCost != null ? String(catalogCost) : p.unit_price,
    }));
    // Load related products and bundles containing this product
    Promise.all([
      api.get(`product-catalog/${item.id}/related`).catch(() => []),
      api.get('product-bundles', { product_id: item.id }).catch(() => []),
    ]).then(([related, bundles]) => {
      setCatalogSuggestions({ related: related || [], bundles: bundles || [] });
    });
  };

  const clearCatalogItem = () => {
    setAddLineCatItem(null);
    setCatalogSuggestions({ related: [], bundles: [] });
    setAddLineForm(p => ({ ...p, product_catalog_id: '', description: '', part_number: '', unit_price: '' }));
  };

  const filteredAddLineCatalog = addLineCatSearch.trim()
    ? (() => {
        const q = addLineCatSearch.toLowerCase();
        // Match the same fields global search does — crucially incl. the
        // manufacturer part number (e.g. "DELL-S3225QS"), which is what people
        // usually type. Null-guarded so a row missing any field can't throw.
        return addLineCatalog.filter(c =>
          (c.description || '').toLowerCase().includes(q) ||
          (c.code || '').toLowerCase().includes(q) ||
          (c.manufacturer || '').toLowerCase().includes(q) ||
          (c.manufacturer_part_number || '').toLowerCase().includes(q)
        );
      })()
    : addLineCatalog.slice(0, 40);

  const submitAddLine = async () => {
    await addLine(detail.id, {
      description:           addLineForm.description,
      part_number:           addLineForm.part_number,
      quantity:              parseInt(addLineForm.quantity) || 1,
      unit_price:            parseFloat(addLineForm.unit_price) || 0,
      product_catalog_id:    addLineForm.product_catalog_id || null,
      line_type:             addLineForm.line_type,
      service_status:        addLineForm.line_type === 'service' ? addLineForm.service_status : undefined,
      service_forecast_date: addLineForm.line_type === 'service' ? (addLineForm.service_forecast_date || null) : undefined,
    });
    closeAddLine();
  };

  const load = () => {
    Promise.all([api.get('purchase-orders'), api.get('suppliers'), api.get('sows'), api.get('delivery-addresses')]).then(([p, s, sw, a]) => {
      setPOs(p); setSuppliers(s); setSows(sw); setAddresses(a);
    });
  };
  useEffect(load, []);

  // Auto-open a specific PO when navigated here with a detailId (e.g. from Hardware Requests).
  // Only fetch once pos are loaded — if the PO isn't in the list, skip to avoid a noisy 404.
  useEffect(() => {
    if (!detailId || pos.length === 0) return;
    if (pos.some(p => p.id === detailId)) viewDetail(detailId);
  }, [detailId, pos]);

  const createPO = async (data) => {
    // POST may throw — including a 422 QA soft-gate, which CreatePOModal catches
    // to prompt for an acknowledgement reason. Post-create steps are best-effort.
    const created = await api.post('purchase-orders', data);
    setShowCreate(false);
    load();
    try {
      const fullPO = await api.get(`purchase-orders/${created.id}`);
      setDetail(fullPO);
      setDetailTab('lines');
    } catch (_) {}
  };

  const viewDetail = useCallback(async (id) => {
    const [d, inv] = await Promise.all([
      api.get(`purchase-orders/${id}`),
      api.get('inventory', { source_po_id: id, limit: 200 }).catch(() => ({ items: [] })),
    ]);
    setDetail(d);
    setPoInventory(inv.items ?? []);
    setPoInventoryTotal(inv.total ?? (inv.items ?? []).length);
    setDetailTab('lines');
    setPoHistory([]);
    setPoNotes([]);
    setNotesLoaded(false);
    setDeliveries([]);
    setDeliveriesLoaded(false);
    setQuoteUploads(d.quote_uploads || []);
    setQuotesLoaded(true);
  }, []);

  const refreshDetail = useCallback(async (id) => {
    const [d, inv] = await Promise.all([
      api.get(`purchase-orders/${id}`),
      api.get('inventory', { source_po_id: id, limit: 200 }).catch(() => ({ items: [] })),
    ]);
    setDetail(d);
    setPoInventory(inv.items ?? []);
    setPoInventoryTotal(inv.total ?? (inv.items ?? []).length);
  }, []);

  const loadHistory = useCallback(async (id) => {
    const h = await api.get(`purchase-orders/${id}/history`).catch(() => []);
    setPoHistory(h);
  }, []);

  const loadNotes = useCallback(async (id) => {
    const notes = await api.get('commentary', { entity_type: 'po', entity_id: id }).catch(() => []);
    setPoNotes(notes);
    setNotesLoaded(true);
  }, []);

  const submitNote = async () => {
    if (!noteText.trim() && !noteAttachment) return;
    setSavingNote(true);
    try {
      await api.post('commentary', {
        entity_type: 'po',
        entity_id: detail.id,
        content: noteText,
        attachment_r2_key: noteAttachment?.key || null,
        attachment_filename: noteAttachment?.filename || null,
        attachment_content_type: noteAttachment?.content_type || null,
      });
      setNoteText('');
      setNoteAttachment(null);
      loadNotes(detail.id);
    } finally {
      setSavingNote(false);
    }
  };

  // Create the stock records this PO never got, so its delivery can be recorded.
  // The dialog stays open: the records it creates are the list it then shows.
  const createMissingStock = async () => {
    setCreatingStock(true);
    setBackfillError('');
    try {
      const res = await api.post(`purchase-orders/${detail.id}/stock/backfill`, { mode: backfillMode });
      await refreshDetail(detail.id);
      toast(`${res.created} stock record${res.created === 1 ? '' : 's'} created — tick off what has arrived`, 'success');
    } catch (e) {
      setBackfillError(e?.data?.error || e.message || 'Could not create the stock records');
    } finally {
      setCreatingStock(false);
    }
  };

  const openAddToStock = () => {
    setAddToStockError('');
    prepareStockChoice(missingStockLines);
    setReceiveLocation('');
    api.get('inventory/locations').then(setLocations).catch(() => setLocations([]));
    setShowAddToStock(true);
  };

  // A delivered PO's goods arrived, so the records go straight into stock (or to
  // the SOW they were bought for) rather than to incoming.
  const submitAddToStock = async () => {
    const lineIds = missingStockLines.filter(l => stockLineIds.has(l.id)).map(l => l.id);
    if (lineIds.length === 0) return;
    setAddingToStock(true);
    setAddToStockError('');
    try {
      const res = await api.post(`purchase-orders/${detail.id}/stock/backfill`, {
        mode: stockMode, line_ids: lineIds, location_id: receiveLocation || null,
      });
      setShowAddToStock(false);
      await refreshDetail(detail.id);
      toast(`${res.created} stock record${res.created === 1 ? '' : 's'} added to Inventory`, 'success');
    } catch (e) {
      setAddToStockError(e?.data?.error || e.message || 'Could not add the stock records');
    } finally {
      setAddingToStock(false);
    }
  };

  const openDelivery = () => {
    setBackfillError('');
    setBackfillMode(defaultInventoryMode(detail?.lines || []));
    setSelectedItems(new Set());
    setReceivedQty({});
    setDeliveryError('');
    setDeliveryDate(new Date().toISOString().split('T')[0]);
    setDeliveryLocation('');
    setDeliveryNotes('');
    api.get('inventory/locations').then(setLocations).catch(() => setLocations([]));
    if (!deliveriesLoaded) {
      api.get(`purchase-orders/${detail.id}/deliveries`).then(d => { setDeliveries(d); setDeliveriesLoaded(true); }).catch(() => {});
    }
    setShowDelivery(true);
  };

  const submitDelivery = async () => {
    if (!deliveryDate || selectedItems.size === 0 || deliveryQtyError) return;
    setSubmittingDelivery(true);
    setDeliveryError('');
    try {
      const res = await api.post(`purchase-orders/${detail.id}/deliveries`, {
        delivery_date: deliveryDate,
        location_id: deliveryLocation || null,
        notes: deliveryNotes || null,
        items: [...selectedItems].map(id => ({ id, quantity: unitsArrived(id) })),
      });
      setShowDelivery(false);
      setDeliveriesLoaded(false);
      await refreshDetail(detail.id);
      load();
      handleRequesterNotification(res?.requester_notification, detail);
    } catch (e) {
      setDeliveryError(e?.data?.error || e.message || 'Could not record the delivery');
    } finally {
      setSubmittingDelivery(false);
    }
  };

  const addLine = async (poId, data) => {
    await api.post(`purchase-orders/${poId}/lines`, data);
    viewDetail(poId);
  };

  // Open the appropriate modal for a transition
  const initiateTransition = (targetStatus) => {
    setTransitionTarget(targetStatus);
    setTransitionError('');
    if (targetStatus === 'cancelled') {
      setCancelReason('');
      setTransitionModal('cancel');
    } else if (targetStatus === 'acknowledged') {
      setExpectedDelivDate('');
      // A record per unit is right for serialised kit and wrong for a pallet of
      // consumables, so the size of the order picks the starting point.
      setInventoryMode(defaultInventoryMode(detail?.lines || []));
      // Pre-populate per-line dates from any existing po_line.expected_delivery
      const initial = {};
      (detail?.lines || []).forEach(l => { if (l.expected_delivery) initial[l.id] = l.expected_delivery; });
      setLineDelivDates(initial);
      setTransitionModal('acknowledge');
    } else {
      if (targetStatus === 'delivered' || targetStatus === 'closed') {
        setServerMissingLines(null);
        prepareStockChoice(missingStockLines);
        setReceiveLocation('');
        api.get('inventory/locations').then(setLocations).catch(() => setLocations([]));
      }
      setTransitionModal('confirm');
    }
  };

  // Start the line picker with every line ticked except services: goods are the
  // usual case, and a charge is one untick away.
  const prepareStockChoice = (lines) => {
    setStockLineIds(new Set(lines.filter(l => l.line_type !== 'service').map(l => l.id)));
    setStockMode(defaultInventoryMode(lines));
  };
  const toggleStockLine = (id) => setStockLineIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const closeTransitionModal = () => {
    setTransitionModal(null);
    setTransitionTarget('');
    setTransitionError('');
  };

  // modeOverride lets the confirm step commit a different inventory mode than the one
  // in state, since a setState would not have landed by the time this reads it. It is
  // type-checked because this is also used bare as an onClick handler, which would
  // otherwise pass the click event in as the override.
  const commitTransition = async (modeOverride) => {
    setTransitioning(true);
    setTransitionError('');
    const payload = { status: transitionTarget };
    if (transitionTarget === 'cancelled') payload.cancellation_reason = cancelReason;
    if (transitionReceivesGoods) {
      if (receiveLocation) payload.location_id = receiveLocation;
      // Always an answer when lines are missing, even "none of them", so the
      // server knows the question was put rather than refusing it.
      if (transitionUnstocked.length > 0) {
        payload.missing_stock = {
          line_ids: transitionUnstocked.filter(l => stockLineIds.has(l.id)).map(l => l.id),
          mode: stockMode,
        };
      }
    }
    if (transitionTarget === 'acknowledged') {
      if (expectedDelivDate) payload.expected_delivery_date = expectedDelivDate;
      if (Object.keys(lineDelivDates).length) payload.line_delivery_dates = lineDelivDates;
      if (acknowledgeComment.trim()) payload.acknowledge_comment = acknowledgeComment.trim();
      payload.inventory_mode = typeof modeOverride === 'string' ? modeOverride : inventoryMode;
    }
    try {
      const res = await api.put(`purchase-orders/${detail.id}`, payload);
      closeTransitionModal();
      load();
      await refreshDetail(detail.id);
      handleRequesterNotification(res?.requester_notification, detail);
    } catch (e) {
      // The server found lines with no stock record that this page did not. Show
      // them in this dialog and let the person choose, rather than failing.
      if (e?.status === 409 && e?.data?.code === 'missing_stock') {
        setServerMissingLines(e.data.lines || []);
        prepareStockChoice(e.data.lines || []);
        return;
      }
      setTransitionError(e.message || 'Transition failed');
    } finally {
      setTransitioning(false);
    }
  };

  // Both routes to "delivered" report back whether the requester was told.
  // A resolved requester just gets a confirmation. An unresolved one opens the
  // manual add — a silent non-send is the thing nobody would ever notice.
  // 'already_notified' and 'suppressed' are deliberately quiet: neither is
  // something the person marking delivery can act on.
  const handleRequesterNotification = (n, po) => {
    if (!n) return;
    if (n.sent) {
      toast.success(`Delivery notification sent to ${n.requester_name || n.requester_email}`);
      return;
    }
    if (n.reason === 'no_requester_email') {
      setNotifyForm({ name: n.requester_name || '', email: '' });
      setNotifyError('');
      setNotifyRequester({ id: po.id, po_number: po.po_number });
      return;
    }
    if (n.reason === 'email_failed') {
      toast.error('Marked as delivered, but the requester notification could not be sent');
    }
  };

  const submitNotifyRequester = async () => {
    if (!notifyForm.email.trim()) { setNotifyError('Enter an email address for the requester'); return; }
    setNotifySending(true);
    setNotifyError('');
    try {
      const res = await api.post(`purchase-orders/${notifyRequester.id}/notify-requester`, {
        name: notifyForm.name.trim() || null,
        email: notifyForm.email.trim(),
      });
      setNotifyRequester(null);
      toast.success(`Delivery notification sent to ${res.requester_name || res.requester_email}`);
      if (detail?.id === notifyRequester.id) refreshDetail(detail.id);
    } catch (e) {
      setNotifyError(e.message || 'The notification could not be sent');
    } finally {
      setNotifySending(false);
    }
  };

  const submitSupplierInvoice = async () => {
    await api.post(`purchase-orders/${detail.id}/supplier-invoices`, {
      ...supplierInvoiceForm,
      amount_excl_vat: parseFloat(supplierInvoiceForm.amount_excl_vat) || 0,
      amount_incl_vat: parseFloat(supplierInvoiceForm.amount_incl_vat) || null,
    });
    setShowRecordInvoice(false);
    setSupplierInvoiceForm({ invoice_number: '', invoice_date: '', amount_excl_vat: '', amount_incl_vat: '', payment_date: '', notes: '' });
    viewDetail(detail.id);
  };

  const deleteSupplierInvoice = async (invoiceId) => {
    if (!confirm('Delete this supplier invoice record?')) return;
    await api.delete(`purchase-orders/${detail.id}/supplier-invoices/${invoiceId}`);
    viewDetail(detail.id);
  };

  const openPDF = () => {
    const html = generatePOHtml(detail);
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const setSIF = (k, v) => setSupplierInvoiceForm(p => ({ ...p, [k]: v }));

  const totalInvoiced = detail?.supplier_invoices?.reduce((s, i) => s + (i.amount_excl_vat || 0), 0) || 0;
  const poTotal = detail?.subtotal || 0;   // subtotal = excl. VAT; total = incl. VAT
  const remaining = poTotal - totalInvoiced;

  // Every amount on the open PO — lines, totals, supplier invoices, stock cost —
  // is in the currency the PO was raised in. Nothing is converted.
  const poCur = (detail?.currency || 'GBP').toUpperCase();
  const poSym = currencySymbol(poCur) || poCur;
  const m = (v, dp = 2) => formatMoney(v, dp, poCur);

  // ── Stock records the open PO is missing ──────────────────────────────────
  // Receiving goods works off inventory records, so a PO with none has nothing to
  // tick off however many lines are still outstanding. These are the lines that
  // would have had a record had the PO been acknowledged with one of the create
  // modes. The truncation guard matters: poInventory stops at 200 rows, and a PO
  // that big is certainly not short of records — reading a capped list as gaps
  // would offer to create duplicates.
  const incomingItems = poInventory.filter(i => i.status === 'incoming');
  const missingStockLines = poInventoryTotal > poInventory.length
    ? []
    : linesMissingStock(detail?.lines || [], poInventory);
  // Nothing to receive and lines with no record: the dialog offers to create them
  // rather than showing an empty list.
  const needsStockRecords = incomingItems.length === 0 && missingStockLines.length > 0;
  // A record acknowledged as a batch holds the whole line, so a part delivery of
  // it says how many units came; the server splits those off and leaves the rest
  // incoming on the original tag.
  const unitsHeld = (item) => Math.max(1, parseInt(item?.quantity) || 1);
  const unitsArrived = (id) => {
    const item = incomingItems.find(i => i.id === id);
    return receivedQty[id] ?? unitsHeld(item);
  };
  const selectedIncoming = incomingItems.filter(i => selectedItems.has(i.id));
  const unitsSelected = selectedIncoming.reduce((n, i) => n + (Number(unitsArrived(i.id)) || 0), 0);
  const partReceived = selectedIncoming.filter(i => Number(unitsArrived(i.id)) < unitsHeld(i));
  const badQty = selectedIncoming.find(i => {
    const q = Number(unitsArrived(i.id));
    return !Number.isInteger(q) || q < 1 || q > unitsHeld(i);
  });
  const deliveryQtyError = badQty
    ? `${badQty.asset_tag}: enter between 1 and ${unitsHeld(badQty)} units`
    : '';
  // Marking delivered, or closing straight from part delivered, is where goods
  // are received. The lines it would leave out of Inventory are the server's
  // list when it has refused once, else what this page can see.
  const transitionReceivesGoods = transitionTarget === 'delivered'
    || (transitionTarget === 'closed' && detail?.status === 'part_delivered');
  const transitionUnstocked = transitionReceivesGoods ? (serverMissingLines || missingStockLines) : [];
  const stockTicked = transitionUnstocked.filter(l => stockLineIds.has(l.id)).length;

  // ── Active / Inactive split + search/filter ───────────────────────────────
  const applyPOSearch = (list) => {
    if (!poSearch.trim()) return list;
    const q = poSearch.trim().toLowerCase();
    return list.filter(p =>
      (p.po_number || '').toLowerCase().includes(q) ||
      (p.supplier_name || '').toLowerCase().includes(q) ||
      (p.sow_reference || '').toLowerCase().includes(q) ||
      (p.notes || '').toLowerCase().includes(q) ||
      (p.requester_name || '').toLowerCase().includes(q) ||
      String(p.subtotal || '').includes(q)
    );
  };
  // Search and status are separate steps because the Active/Closed tab counts
  // are taken from the search alone. Counting those through the status filter
  // would read "Active (0)" the moment somebody picked a status that only
  // exists in the other tab, which looks like the tab has emptied.
  const applyPOFilters = (list) => {
    const searched = applyPOSearch(list);
    if (statusFilter === 'all') return searched;
    return searched.filter(p => p.status === statusFilter);
  };
  const activePOs   = pos.filter(p => !INACTIVE_STATUSES.has(p.status));
  const inactivePOs = pos.filter(p =>  INACTIVE_STATUSES.has(p.status));

  // Committed value of active POs, split by currency — sterling first, then any
  // foreign currency in use. Unlike currencies are never summed together.
  const activeValueByCurrency = (() => {
    const totals = new Map();
    for (const p of activePOs) {
      const code = (p.currency || 'GBP').toUpperCase();
      totals.set(code, (totals.get(code) || 0) + (p.subtotal || 0));
    }
    return [...totals.entries()].sort((a, b) => (a[0] === 'GBP' ? -1 : b[0] === 'GBP' ? 1 : b[1] - a[1]));
  })();
  const tabPOs      = applyPOFilters(listTab === 'active' ? activePOs : inactivePOs);

  // A chip per status actually present in the open tab, so no tab ever carries
  // a chip that can only return nothing.
  const statusFilterOptions = (() => {
    const inTab = applyPOSearch(listTab === 'active' ? activePOs : inactivePOs);
    const counts = inTab.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});
    return [
      { key: 'all', label: 'All', count: inTab.length },
      ...PO_STATUSES
        .filter(st => counts[st] > 0)
        .map(st => ({ key: st, label: STATUS_LABELS[st], count: counts[st] })),
    ];
  })();

  const sortedTabPOs = (() => {
    const { key, dir } = poSort;
    if (!key) return tabPOs;
    return [...tabPOs].sort((a, b) => {
      let av = key === 'remaining' ? (a.subtotal || 0) - (a.total_invoiced || 0) : a[key];
      let bv = key === 'remaining' ? (b.subtotal || 0) - (b.total_invoiced || 0) : b[key];
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === 'asc' ? av - bv : bv - av;
    });
  })();

  // Reset to page 0 when switching tabs handled via setListTab wrapper below
  const totalPages  = Math.ceil(sortedTabPOs.length / PAGE_SIZE);
  const pagedPOs    = sortedTabPOs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // The status filter is scoped to the open tab, so it is cleared on the way
  // across rather than left pointing at a status the new tab cannot show.
  const switchTab = (t) => { setListTab(t); setStatusFilter('all'); setPage(0); };

  const downloadPOExcel = () => {
    const wb = XLSX.utils.book_new();
    // Amounts are unconverted, so the currency column is what makes the value
    // columns readable — don't total them across rows without it.
    const headers = ['PO #', 'Supplier', 'SOW', 'Status', 'Issued Date', 'Requested By', 'Currency', 'Total excl VAT', 'Invoiced', 'Acknowledged', 'Notes'];
    const rows = sortedTabPOs.map(p => [
      p.po_number, p.supplier_name || '', p.sow_reference || '',
      STATUS_LABELS[p.status] || p.status, p.order_date || '', p.requester_name || '',
      (p.currency || 'GBP').toUpperCase(),
      p.subtotal || 0, p.total_invoiced || 0, p.acknowledged_at?.slice(0,10) || '', p.notes || '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [12, 20, 12, 14, 18, 12, 10, 14, 14, 14, 40].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, 'Purchase Orders');
    XLSX.writeFile(wb, `purchase-orders-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  // The wrapper, not POList, carries the scroll: POList is re-created on every
  // render, so a scroll region inside it would be remounted and snap back to
  // the top on each keystroke.
  const listScrollClass = detail ? 'flex-1 min-h-0 overflow-y-auto -mr-1 pr-1' : '';
  const POList = ({ compact }) => (
    <>
      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden overflow-x-auto">
        <table className="w-full">
          {!compact && (() => {
            const cols = [
              { key: 'po_number',      label: 'PO #',             align: 'left'  },
              { key: 'supplier_name',  label: 'Supplier',          align: 'left'  },
              { key: 'sow_reference',  label: 'SOW',               align: 'left'  },
              { key: 'status',         label: 'Status',            align: 'left'  },
              { key: 'order_date',     label: 'Issued Date',       align: 'left'  },
              { key: 'requester_name', label: 'Requested By',      align: 'left'  },
              { key: 'subtotal',       label: 'Total (excl. VAT)', align: 'right' },
              { key: 'total_invoiced', label: 'Invoiced',          align: 'right' },
              { key: 'remaining',      label: 'Remaining',         align: 'right' },
              { key: null,             label: '',                  align: 'left'  },
            ];
            const toggleSort = (key) => {
              if (!key) return;
              setPoSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
              setPage(0);
            };
            return (
              <thead><tr className="bg-gray-50">
                {cols.map(({ key, label, align }) => (
                  <th key={label || '_action'} onClick={() => toggleSort(key)}
                    className={`px-2 py-1.5 text-[8px] uppercase text-gray-500 font-bold border-b select-none text-${align} ${key ? 'cursor-pointer hover:text-navy hover:bg-gray-100 transition-colors' : ''}`}>
                    {label}
                    {key && poSort.key === key && (
                      <span className="ml-0.5 text-gold">{poSort.dir === 'asc' ? '▲' : '▼'}</span>
                    )}
                    {key && poSort.key !== key && <span className="ml-0.5 opacity-0 group-hover:opacity-30">▲</span>}
                  </th>
                ))}
              </tr></thead>
            );
          })()}
          <tbody>
            {pagedPOs.map(p => {
              const isSelected = detail?.id === p.id;
              if (compact) return (
                <tr key={p.id}
                  onClick={() => viewDetail(p.id)}
                  className={`border-b border-gray-100 cursor-pointer transition-colors ${isSelected ? 'bg-gold/10 border-l-2 border-l-gold' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-2 py-2">
                    <div className="text-[10px] font-bold text-gold">{p.po_number}</div>
                    <div className="text-[8px] text-gray-500 truncate">{p.supplier_name || '—'}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <StatusBadge status={p.status} />
                      <span className="text-[8px] font-semibold text-gray-600">{formatMoney(p.subtotal, 2, p.currency)}</span>
                    </div>
                  </td>
                </tr>
              );
              return (
                <tr key={p.id} onClick={() => viewDetail(p.id)}
                  className={`border-b border-gray-100 cursor-pointer transition-colors ${isSelected ? 'bg-gold/10' : 'hover:bg-gold/5'}`}
                >
                  <td className="px-2 py-1.5 text-[10px] font-bold text-gold">{p.po_number}</td>
                  <td className="px-2 py-1.5 text-[10px]">{p.supplier_name || '—'}</td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.sow_reference || '—'}</td>
                  <td className="px-2 py-1.5"><StatusBadge status={p.status} /></td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.order_date ? formatDate(p.order_date) : '—'}</td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.requester_name || '—'}</td>
                  <td className="px-2 py-1.5 text-[10px] font-bold text-right">{formatMoney(p.subtotal, 2, p.currency)}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right text-blue-600">{p.total_invoiced > 0 ? formatMoney(p.total_invoiced, 2, p.currency) : '—'}</td>
                  <td className={`px-2 py-1.5 text-[10px] font-semibold text-right ${p.total_invoiced > p.subtotal ? 'text-red-600' : 'text-gray-500'}`}>
                    {p.total_invoiced > 0 ? formatMoney((p.subtotal || 0) - (p.total_invoiced || 0), 2, p.currency) : !INACTIVE_STATUSES.has(p.status) && p.status !== 'draft' ? formatMoney(p.subtotal || 0, 2, p.currency) : '—'}
                    {p.total_invoiced > p.subtotal && <span className="ml-0.5 text-red-500 text-[7px]">⚠</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <svg className="w-3.5 h-3.5 text-gray-300 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </td>
                </tr>
              );
            })}
            {pagedPOs.length === 0 && (
              <tr><td colSpan={compact ? 1 : 10} className="text-center py-6 text-[10px] text-gray-400">
                {poSearch || statusFilter !== 'all' ? 'No purchase orders match these filters.' : listTab === 'active' ? 'No active purchase orders' : 'No inactive purchase orders'}
              </td></tr>
            )}
            {/* colSpan updated to 10 to match new Issued Date column */}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-1 mt-2">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
            className="text-[9px] font-semibold border border-gray-200 rounded px-2 py-0.5 disabled:opacity-30 hover:bg-gray-50 disabled:cursor-not-allowed bg-white">‹</button>
          <span className="text-[8px] text-gray-400">{page + 1}/{totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
            className="text-[9px] font-semibold border border-gray-200 rounded px-2 py-0.5 disabled:opacity-30 hover:bg-gray-50 disabled:cursor-not-allowed bg-white">›</button>
        </div>
      )}
    </>
  );

  return (
    <div className="fade-in">
      <div className={`flex gap-4 items-start ${detail ? '' : ''}`}>

        {/* ── LEFT: list panel ──────────────────────────────────────────────── */}
        {/* With a PO open the list is pinned beside it and scrolls in its own
            right, so working down a long list never drags the order you are
            reading off the top of the screen. */}
        <div className={detail
          ? 'hidden md:flex md:flex-col w-64 flex-shrink-0 md:sticky md:top-0 md:max-h-[78vh]'
          : 'flex-1 min-w-0'}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-extrabold text-navy">Purchase Orders</h2>
              {!detail && <div className="text-[9px] text-gray-500">{pos.length} POs total</div>}
            </div>
            {!detail && (
              <div className="flex gap-2">
                <button onClick={() => setShowManageAddresses(true)} className="px-2 py-1.5 bg-white border border-gray-200 text-navy text-[10px] font-semibold rounded hover:bg-gray-50">Delivery Addresses</button>
                <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ New PO</button>
              </div>
            )}
            {detail && (
              <button onClick={() => setShowCreate(true)} className="px-2 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80">+ New</button>
            )}
          </div>

          {/* Summary cards — only in full view */}
          {!detail && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">Active</div><div className="text-lg font-extrabold text-navy">{activePOs.length}</div></div>
              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="text-[8px] text-gray-500 uppercase font-bold">Value (excl. VAT)</div>
                {/* One figure per currency — POs raised in EUR/USD are never added
                    into the sterling total, since nothing here is converted. */}
                {activeValueByCurrency.map(([code, value], i) => (
                  <div key={code} className={i === 0 ? 'text-lg font-extrabold text-navy' : 'text-[10px] font-bold text-gray-500'}>
                    {formatMoney(value, 2, code)}
                  </div>
                ))}
                {activeValueByCurrency.length === 0 && <div className="text-lg font-extrabold text-navy">{formatMoney(0)}</div>}
              </div>
              <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">Draft</div><div className="text-lg font-extrabold text-amber-500">{pos.filter(p => p.status === 'draft').length}</div></div>
              <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">Issued</div><div className="text-lg font-extrabold text-blue-500">{pos.filter(p => p.status === 'issued').length}</div></div>
            </div>
          )}

          {/* Search & column filters */}
          {!detail && (
            <div className="flex flex-wrap gap-2 mb-2">
              <input
                className="form-input flex-1 text-[10px]"
                placeholder="🔍 Search PO #, supplier, SOW ref, notes…"
                value={poSearch}
                onChange={e => { setPoSearch(e.target.value); setPage(0); }}
              />
              <button onClick={downloadPOExcel} className="px-2 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 whitespace-nowrap">
                ↓ Excel ({tabPOs.length})
              </button>
              {poSearch && (
                <button onClick={() => { setPoSearch(''); setPage(0); }} className="px-2 py-1 text-[9px] text-gray-500 border border-gray-200 rounded hover:bg-gray-50">
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Active/Inactive tabs */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex border border-gray-200 rounded overflow-hidden">
              {[['active', `Active (${applyPOSearch(activePOs).length})`], ['inactive', `Closed (${applyPOSearch(inactivePOs).length})`]].map(([key, label]) => (
                <button key={key} onClick={() => switchTab(key)}
                  className={`px-2 py-1 text-[9px] font-semibold transition-colors ${listTab === key ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >{label}</button>
              ))}
            </div>
          </div>

          {/* Status chips. Deliberately outside the `!detail` guard that hides
              the summary cards and the search box, so the list can still be
              narrowed while a PO is open beside it. Only shown once the tab
              holds more than one status, since a lone chip decides nothing. */}
          {statusFilterOptions.length > 2 && (
            <StatusFilter
              value={statusFilter}
              onChange={(k) => { setStatusFilter(k); setPage(0); }}
              options={statusFilterOptions}
            />
          )}

          <div className={listScrollClass}>
            <POList compact={!!detail} />
          </div>
        </div>

        {/* ── RIGHT: detail panel ───────────────────────────────────────────── */}
        {detail && (
          <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">

            {/* ── Sticky header bar ── */}
            <div className="flex items-center justify-between px-4 py-3 bg-navy text-white">
              <div className="flex items-center gap-3">
                <button onClick={() => setDetail(null)} className="md:hidden -ml-1 mr-1 text-white/70 hover:text-white text-[11px] font-semibold">← Back</button>
                <span className="text-[15px] font-extrabold tracking-wide text-gold">{detail.po_number}</span>
                <CopyButton text={detail.po_number} label="Copy PO number to clipboard" light className="-ml-2" />
                <StatusBadge status={detail.status} />
                {/* Only flagged when it isn't sterling — that's the case people
                    need to spot before reading any figure on this PO. */}
                {poCur !== 'GBP' && (
                  <span className="text-[8px] font-bold bg-white/15 text-white px-1.5 py-0.5 rounded border border-white/25" title={`This PO is raised in ${poCur}`}>
                    {poSym} {poCur}
                  </span>
                )}
                {detail.revision_count > 0 && (
                  <span className="text-[8px] font-bold bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded border border-amber-400/30">
                    Rev. {detail.revision_count} · edited {detail.last_edited_by_name || ''}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {['acknowledged', 'part_delivered'].includes(detail.status) && (incomingItems.length > 0 || missingStockLines.length > 0) && (
                  <button onClick={openDelivery} className="px-2.5 py-1 text-[9px] font-semibold bg-emerald-500 hover:bg-emerald-600 rounded text-white transition-colors">
                    Record Delivery
                  </button>
                )}
                {['delivered', 'closed'].includes(detail.status) && missingStockLines.length > 0 && (
                  <button onClick={openAddToStock}
                    title={`${missingStockLines.length} line${missingStockLines.length !== 1 ? 's have' : ' has'} no stock record`}
                    className="px-2.5 py-1 text-[9px] font-semibold bg-emerald-500 hover:bg-emerald-600 rounded text-white transition-colors">
                    Add to Stock
                  </button>
                )}
                {EDITABLE_STATUSES.has(detail.status) && (
                  <button onClick={() => setShowEditPO(true)} className="px-2.5 py-1 text-[9px] font-semibold bg-white/10 hover:bg-white/20 rounded text-white border border-white/20 transition-colors">
                    Edit PO
                  </button>
                )}
                <button onClick={openPDF} className="px-2.5 py-1 text-[9px] font-semibold bg-white/10 hover:bg-white/20 rounded text-white border border-white/20 transition-colors">
                  🖨 Print
                </button>
                <button onClick={() => setDetail(null)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 text-white/70 hover:text-white text-lg leading-none transition-colors" title="Close">×</button>
              </div>
            </div>

            {/* ── Metadata row ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-b border-gray-100 bg-gray-50/50">
              <div className="px-4 py-2.5 border-r border-gray-100">
                <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">Supplier</div>
                {detail.status === 'draft' ? (
                  <select className="w-full text-[10px] font-semibold text-navy bg-transparent border-b border-gray-300 focus:border-gold outline-none pb-0.5"
                    value={detail.supplier_id || ''}
                    onChange={async e => { await api.put(`purchase-orders/${detail.id}`, { supplier_id: e.target.value || null }); viewDetail(detail.id); }}>
                    <option value="">— none —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : detail.supplier_id && detail.supplier_name ? (
                  <button onClick={() => navigate('suppliers', detail.supplier_id)}
                    className="text-[10px] font-semibold text-navy hover:text-gold hover:underline text-left" title="View supplier">{detail.supplier_name}</button>
                ) : (
                  <div className="text-[10px] font-semibold text-navy">{detail.supplier_name || '—'}</div>
                )}
              </div>
              <div className="px-4 py-2.5 border-r border-gray-100">
                <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">SOW</div>
                {detail.status === 'draft' ? (
                  <SowSelect
                    sows={sows}
                    value={detail.sow_id || ''}
                    onChange={async id => { await api.put(`purchase-orders/${detail.id}`, { sow_id: id || null }); viewDetail(detail.id); }}
                    placeholder="— none —"
                    inputClassName="w-full text-[10px] font-semibold text-navy bg-transparent border-b border-gray-300 focus:border-gold outline-none pb-0.5"
                  />
                ) : detail.sow_id && detail.sow_reference ? (
                  <button onClick={() => navigate('sow-detail', detail.sow_id)}
                    className="text-[10px] font-semibold text-navy hover:text-gold hover:underline text-left" title="View SOW">{detail.sow_reference}</button>
                ) : (
                  <div className="text-[10px] font-semibold text-navy">{detail.sow_reference || '—'}</div>
                )}
              </div>
              {[
                ['Order Date', formatDate(detail.order_date) || '—'],
                ['Exp. Delivery', detail.expected_delivery_date ? formatDate(detail.expected_delivery_date) : '—'],
              ].map(([label, value]) => (
                <div key={label} className="px-4 py-2.5 border-r border-gray-100 last:border-r-0">
                  <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">{label}</div>
                  <div className="text-[10px] font-semibold text-navy">{value}</div>
                </div>
              ))}
            </div>

            {/* A PO covers one SOW. Raising one that spans several is refused
                now, but the POs raised before that rule still exist and give
                nothing away: the SOW field above is empty and the only trace is
                the note underneath. Say it plainly, because stock bought here
                inherits the PO's SOW and so arrives unallocated. */}
            {(() => {
              const spread = poSowSpread(detail);
              return spread.spans && (
                <div className="px-4 py-2 border-b border-amber-200 bg-amber-100/70">
                  <span className="text-[8px] font-bold uppercase text-amber-700 mr-2">Check the SOW</span>
                  <span className="text-[9px] text-amber-900">
                    This PO looks like it covers {spread.refs.length} SOWs ({spread.refs.join(', ')}).
                    A PO should be raised against one, and stock received here takes the PO's SOW,
                    so anything for the others will need allocating by hand.
                  </span>
                </div>
              );
            })()}

            {detail.notes && (
              <div className="px-4 py-2 border-b border-gray-100 bg-amber-50/50">
                <span className="text-[8px] font-bold uppercase text-amber-600 mr-2">Notes</span>
                <span className="text-[9px] text-gray-600">{detail.notes}</span>
              </div>
            )}

            {detail.cancellation_reason && (
              <div className="px-4 py-2 border-b border-red-100 bg-red-50">
                <span className="text-[8px] font-bold uppercase text-red-500 mr-2">Cancelled</span>
                <span className="text-[9px] text-red-700">{detail.cancellation_reason}</span>
              </div>
            )}

            {/* Originating hardware request(s) — what was asked for, by whom */}
            {(detail.hardware_requests || []).length > 0 && (
              <div className="px-4 py-2.5 border-b border-indigo-100 bg-indigo-50/50">
                <div className="text-[8px] font-bold uppercase text-indigo-600 mb-1.5">
                  From hardware request{detail.hardware_requests.length > 1 ? 's' : ''}
                </div>
                <div className="space-y-1.5">
                  {detail.hardware_requests.map(hr => (
                    <div key={hr.id} className="text-[9px] text-gray-700 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span className="font-semibold text-gray-800">{hr.product_name}</span>
                      {hr.part_number && <span className="font-mono text-gray-500">PN: {hr.part_number}</span>}
                      <span className="text-gray-500">Qty: {hr.quantity || 1}</span>
                      {/* The request's own SOW, so a PO that quietly spans two is
                          visible line by line and not only in the banner above. */}
                      {hr.sow_reference && (
                        <span className={hr.sow_reference !== detail.sow_reference && detail.sow_reference
                          ? 'font-semibold text-amber-700' : 'font-semibold text-navy'}>
                          {hr.sow_reference}
                        </span>
                      )}
                      {hr.requester_name && <span>Requested by: <span className="font-semibold">{hr.requester_name}</span></span>}
                      {(hr.supplier_name || hr.supplier_name_free) && (
                        <span>Supplier: <span className="font-semibold">{hr.supplier_name || hr.supplier_name_free}</span>{!hr.supplier_name && hr.supplier_name_free ? <span className="text-amber-600"> (proposed)</span> : null}</span>
                      )}
                      {hr.product_url && <a href={hr.product_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">product link ↗</a>}
                      {hr.sow_reason && <span className="text-gray-400 italic">{hr.sow_reason}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Financial summary ── */}
            <div className={`flex items-center gap-6 px-4 py-3 border-b ${remaining < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
              <div>
                <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">PO Value{poCur !== 'GBP' ? ` (${poCur})` : ''}</div>
                <div className="text-[14px] font-extrabold text-navy">{m(poTotal)}</div>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div>
                <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">Invoiced{(detail.supplier_invoices || []).length > 0 ? ` (${(detail.supplier_invoices || []).length})` : ''}</div>
                <div className="text-[14px] font-extrabold text-blue-600">{m(totalInvoiced)}</div>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div>
                <div className={`text-[7px] font-bold uppercase mb-0.5 ${remaining < 0 ? 'text-red-500' : 'text-gray-400'}`}>{remaining < 0 ? '⚠ Over-invoiced' : 'Remaining'}</div>
                <div className={`text-[14px] font-extrabold ${remaining < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{m(Math.abs(remaining))}</div>
              </div>
              {remaining < 0 && <span className="ml-auto text-[9px] text-red-500 font-semibold bg-red-100 px-2 py-1 rounded">Do not pay without authorisation</span>}
            </div>

            {/* ── Workflow stepper ── */}
            {(() => {
              const steps = ['draft', 'issued', 'acknowledged', 'part_delivered', 'delivered', 'closed'];
              const currentIdx = steps.indexOf(detail.status);
              const nextSteps = VALID_TRANSITIONS[detail.status] || [];
              const canCancel = nextSteps.includes('cancelled');
              if (detail.status === 'cancelled') {
                return (
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-red-100 bg-red-50/50">
                    <span className="text-[8px] font-bold text-red-500 uppercase">Status: Cancelled</span>
                    {detail.cancellation_reason && <span className="text-[8px] text-red-400">— {detail.cancellation_reason}</span>}
                  </div>
                );
              }
              return (
                <div className="flex items-center gap-0 px-4 py-2 border-b border-gray-100 bg-gray-50/30 overflow-x-auto">
                  <div className="flex items-center gap-0 flex-shrink-0">
                    {steps.map((s, i) => {
                      const isPast = currentIdx !== -1 && i < currentIdx;
                      const isCurrent = i === currentIdx;
                      const isNext = nextSteps.includes(s);
                      return (
                        <div key={s} className="flex items-center">
                          {i > 0 && (
                            <div className={`w-5 h-px flex-shrink-0 ${isPast ? 'bg-navy' : 'bg-gray-200'}`} />
                          )}
                          <button
                            onClick={isNext ? () => initiateTransition(s) : undefined}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-[8px] font-bold whitespace-nowrap transition-colors flex-shrink-0
                              ${isCurrent
                                ? 'bg-navy text-white shadow-sm'
                                : isPast
                                  ? 'text-navy/50 cursor-default'
                                  : isNext
                                    ? 'border border-navy/40 text-navy bg-white hover:bg-navy hover:text-white cursor-pointer shadow-sm'
                                    : 'text-gray-300 cursor-default'
                              }`}
                          >
                            {isPast && <span className="text-[7px]">✓</span>}
                            {STATUS_LABELS[s]}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {canCancel && (
                    <button onClick={() => initiateTransition('cancelled')}
                      className="ml-auto flex-shrink-0 px-2.5 py-1 text-[8px] font-semibold rounded border border-red-200 text-red-500 bg-white hover:bg-red-50 transition-colors">
                      Cancel PO
                    </button>
                  )}
                </div>
              );
            })()}

            {/* ── Tabs ── */}
            <div className="flex border-b border-gray-200 px-4">
              {[
                ['lines',    `Order Lines (${(detail.lines || []).length})`],
                ['invoices', `Invoices${(detail.supplier_invoices || []).length > 0 ? ` (${(detail.supplier_invoices || []).length})` : ''}`],
                ...(poInventoryTotal > 0 ? [['stock', `Stock (${poInventoryTotal})`]] : []),
                ['quotes',   `Quotes${quoteUploads.length > 0 ? ` (${quoteUploads.length})` : ''}`],
                ['notes',    'Notes & Docs'],
                ['history',  'History'],
              ].map(([t, label]) => (
                <button key={t}
                  className={`px-3 py-2.5 text-[10px] font-medium border-b-2 -mb-px transition-colors ${detailTab === t ? 'text-gold border-gold font-semibold' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                  onClick={() => {
                    setDetailTab(t);
                    if (t === 'history' && poHistory.length === 0) loadHistory(detail.id);
                    if (t === 'notes' && !notesLoaded) loadNotes(detail.id);
                  }}
                >{label}</button>
              ))}
            </div>

            {/* ── Tab content ── */}
            <div className="p-4">

              {detailTab === 'lines' && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[9px] font-bold uppercase text-gray-400">{(detail.lines || []).length} line{(detail.lines || []).length !== 1 ? 's' : ''}</span>
                    {!INACTIVE_STATUSES.has(detail.status) && (
                      <button onClick={openAddLine} className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Add Line</button>
                    )}
                  </div>
                  {!INACTIVE_STATUSES.has(detail.status) && detail.status !== 'draft' && (
                    <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-[9px] text-amber-700">
                      This PO has already been issued. Adding, editing or removing lines is recorded as a tracked revision — use it to correct historic data, then re-issue the PO PDF if the supplier needs the updated version.
                    </div>
                  )}
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Item / Part Number</th>
                        <th className="px-3 py-2 text-center text-[8px] uppercase text-gray-500 border-b font-bold w-16">Qty</th>
                        <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 border-b font-bold w-28">Unit Price</th>
                        <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 border-b font-bold w-28">Line Total</th>
                        {!INACTIVE_STATUSES.has(detail.status) && <th className="px-3 py-2 border-b w-16" />}
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.lines || []).map((l, i) => (
                        editingLineId === l.id ? (
                          <tr key={l.id} className="border-b border-gold/30 bg-amber-50/30">
                            <td className="px-3 py-2" colSpan="5">
                              <div className="space-y-2">
                                {/* Catalog link */}
                                <div className="relative">
                                  {editLineCatItem ? (
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-[10px]">
                                      <span className="font-mono font-bold text-navy">{editLineCatItem.code || editLineCatItem.manufacturer_part_number || ''}</span>
                                      <span className="text-gray-600 truncate">{editLineCatItem.description}</span>
                                      <button type="button" onClick={() => { setEditLineCatItem(null); setELF('product_catalog_id', ''); }} className="ml-auto text-gray-400 hover:text-red-500 text-xs leading-none">✕</button>
                                    </div>
                                  ) : (
                                    <>
                                      <input className="form-input text-[10px] py-1" placeholder="Search product catalogue (optional)…" value={editLineCatSearch}
                                        onChange={e => setEditLineCatSearch(e.target.value)} />
                                      {filteredEditLineCatalog.length > 0 && (
                                        <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-36 overflow-y-auto">
                                          {filteredEditLineCatalog.map(c => (
                                            <button key={c.id} type="button" onMouseDown={() => {
                                              setEditLineCatItem(c);
                                              setEditLineCatSearch('');
                                              setELF('product_catalog_id', c.id);
                                              setELF('description', c.description);
                                              setELF('part_number', c.manufacturer_part_number || c.code || '');
                                              // PO cost = what we pay: catalog cost, else supplier price range (min_price). Never sell_price.
                                              setELF('unit_price', c.unit_cost ?? c.min_price ?? editLineForm.unit_price);
                                            }}
                                              className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-[10px] flex items-center gap-2">
                                              <span className="font-mono text-gray-500 whitespace-nowrap">{c.code}</span>
                                              <span className="truncate">{c.description}</span>
                                              <span className="ml-auto text-gray-400 whitespace-nowrap">{c.manufacturer}</span>
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <input className="form-input text-[10px] py-1 flex-1 min-w-[160px]" placeholder="Description" value={editLineForm.description} onChange={e => setELF('description', e.target.value)} />
                                  <label className="text-[8px] text-gray-400 uppercase font-bold">Qty</label>
                                  <input className="form-input text-[10px] py-1 w-16" type="number" min="1" placeholder="Qty" value={editLineForm.quantity} onChange={e => setELF('quantity', e.target.value)} />
                                  <label className="text-[8px] text-gray-400 uppercase font-bold">Unit {poSym}</label>
                                  <input className="form-input text-[10px] py-1 w-28" type="number" step="0.01" placeholder="0.00" value={editLineForm.unit_price} onChange={e => setELF('unit_price', e.target.value)} />
                                  <div className="flex gap-1.5 items-center ml-auto">
                                    <button onClick={saveEditLine} className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">Save</button>
                                    <button onClick={() => setEditingLineId(null)} className="px-3 py-1 text-[9px] border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={l.id} className={`border-b border-gray-100 hover:bg-gray-50/50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[11px] font-medium text-gray-800">{l.description}</span>
                                {l.line_type === 'service' && (
                                  <span className={`text-[7px] font-bold px-1 py-0.5 rounded uppercase ${
                                    l.service_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                    l.service_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                    l.service_status === 'on_hold' ? 'bg-gray-100 text-gray-500' :
                                    l.service_status === 'cancelled' ? 'bg-red-100 text-red-600' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>{l.service_status || 'outstanding'}</span>
                                )}
                              </div>
                              {l.part_number && <div className="text-[9px] text-gray-400 font-mono mt-0.5">{l.part_number}</div>}
                              {l.line_type === 'service' && l.service_forecast_date && (
                                <div className="text-[8px] text-gray-400 mt-0.5">Forecast: {formatDate(l.service_forecast_date)}</div>
                              )}
                              {l.catalog_code && (
                                <button onClick={() => navigate('product-catalog', l.product_catalog_id)}
                                  className="mt-0.5 inline-flex items-center gap-1 text-[8px] bg-blue-50 border border-blue-200 text-blue-700 px-1.5 py-0.5 rounded hover:bg-blue-100 font-mono" title="View catalogue product">
                                  🔗 {l.catalog_code}{l.catalog_description ? ` — ${l.catalog_description}` : ''}
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-[10px] text-center text-gray-600 font-semibold">{l.quantity}</td>
                            <td className="px-3 py-2.5 text-[10px] text-right text-gray-600">{m(l.unit_price)}</td>
                            <td className="px-3 py-2.5 text-[11px] font-bold text-right text-navy">{m(l.line_total)}</td>
                            {!INACTIVE_STATUSES.has(detail.status) && (
                              <td className="px-2 py-2.5 text-right whitespace-nowrap">
                                <button onClick={() => openEditLine(l)} className="text-gray-400 hover:text-navy px-1" title="Edit line">✏</button>
                                <button onClick={() => deleteLine(l.id)} className="text-gray-300 hover:text-red-500 px-1" title="Delete line">🗑</button>
                              </td>
                            )}
                          </tr>
                        )
                      ))}
                      {(detail.lines || []).length === 0 && (
                        <tr><td colSpan={!INACTIVE_STATUSES.has(detail.status) ? 5 : 4} className="text-center py-8 text-[10px] text-gray-400">No lines yet — use + Add Line to get started</td></tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 bg-gray-50">
                        <td colSpan={!INACTIVE_STATUSES.has(detail.status) ? 4 : 3} className="px-3 py-2 text-right text-[9px] font-bold text-gray-500 uppercase">Total (excl. VAT){poCur !== 'GBP' ? ` (${poCur})` : ''}</td>
                        <td className="px-3 py-2 text-right text-[14px] font-extrabold text-navy">{m(detail.subtotal)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                </>
              )}

              {detailTab === 'invoices' && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[9px] font-bold uppercase text-gray-400">Supplier Invoices Received</span>
                    <button onClick={() => setShowRecordInvoice(true)} className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Record Invoice</button>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Invoice #</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Invoice Date</th>
                        <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 border-b font-bold">Excl. VAT</th>
                        <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 border-b font-bold">Incl. VAT</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Status</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Recorded By</th>
                        <th className="px-3 py-2 border-b"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.supplier_invoices || []).map(inv => (
                        <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 text-[10px] font-mono font-semibold">
                            {inv.invoice_number || '—'}
                            {inv.source === 'invoice_inbox' && (
                              <span className="ml-1.5 font-sans text-[7px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded"
                                title="Recorded against this PO on the invoice form. Change it there.">
                                {inv.sage_invoice_id ? 'Posted to Sage' : 'Invoice form'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-[10px]">{formatDate(inv.invoice_date)}</td>
                          <td className="px-3 py-2 text-[11px] font-semibold text-right text-navy">{m(inv.amount_excl_vat)}</td>
                          <td className="px-3 py-2 text-[10px] text-right text-gray-500">{inv.amount_incl_vat ? m(inv.amount_incl_vat) : '—'}</td>
                          <td className="px-3 py-2">
                            {inv.payment_date
                              ? <span className="text-[9px] font-semibold text-emerald-600">Paid {formatDate(inv.payment_date)}</span>
                              : inv.sage_payment_status === 'PART_PAID'
                              ? <span className="text-[9px] font-semibold text-amber-600">Part paid</span>
                              : <span className="text-[9px] font-semibold text-amber-500">Unpaid</span>}
                          </td>
                          <td className="px-3 py-2 text-[9px] text-gray-500">{inv.recorded_by_name || '—'}</td>
                          <td className="px-3 py-2">
                            {inv.source !== 'invoice_inbox' && (
                              <button onClick={() => deleteSupplierInvoice(inv.id)} className="text-[10px] text-red-400 hover:text-red-600">✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(detail.supplier_invoices || []).length === 0 && (
                        <tr><td colSpan="7" className="text-center py-8 text-[10px] text-gray-400">No supplier invoices recorded yet</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </>
              )}

              {detailTab === 'stock' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[9px] text-gray-500 bg-amber-50 border border-amber-100 rounded px-3 py-2 flex-1 mr-3">
                      Inventory records created when this PO was acknowledged. They will move to <strong>In Stock</strong> automatically when the PO is marked as Delivered.
                      {detail.sow_reference && <span className="ml-2">Linked to <strong>{detail.sow_reference}</strong> — SOW reference is shown on each item.</span>}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                    {poInventoryTotal > (detail.lines || []).length && (
                      <button
                        disabled={consolidating}
                        onClick={async () => {
                          setConsolidating(true);
                          try {
                            // Ask what would happen first, so the confirm names the real
                            // numbers rather than asking anyone to delete on trust.
                            const plan = await api.post(`purchase-orders/${detail.id}/stock/consolidate`, { dry_run: true });
                            const skipped = plan.lines.filter(l => l.skipped);
                            if (!plan.total_deleted) {
                              toast(skipped.length
                                ? 'Nothing to consolidate: a part-delivered line keeps its received and incoming units apart'
                                : 'Stock records are already one per line', 'success');
                              return;
                            }
                            const summary = plan.lines.filter(l => !l.skipped)
                              .map(l => `  ${l.description}: ${l.records_found} records → 1 of qty ${l.line_quantity}`)
                              .concat(skipped.length ? [`  Left as they are, part delivered: ${skipped.map(l => l.description).join(', ')}`] : [])
                              .join('\n');
                            if (!confirm(
                              `Collapse this PO's stock records to one per line, as "single batch per line" would have created them?

${summary}

`
                              + `${plan.total_deleted} record${plan.total_deleted === 1 ? '' : 's'} and their movement history will be deleted. This cannot be undone.`
                            )) return;
                            const res = await api.post(`purchase-orders/${detail.id}/stock/consolidate`, { dry_run: false });
                            await refreshDetail(detail.id);
                            toast(`${res.total_deleted} record${res.total_deleted === 1 ? '' : 's'} removed, ${res.lines.length} kept`, 'success');
                          } catch (e) {
                            toast(e?.data?.error || e.message || 'Could not consolidate stock records', 'error');
                          } finally { setConsolidating(false); }
                        }}
                        className="px-3 py-1.5 text-[9px] font-semibold text-navy border border-navy/30 rounded hover:bg-navy/5 disabled:opacity-40 whitespace-nowrap"
                        title="One record per PO line carrying the ordered quantity, instead of one per unit"
                      >
                        {consolidating ? 'Working…' : 'Consolidate to one per line'}
                      </button>
                    )}
                    {poInventory.some(i => i.status === 'incoming') && (
                      <button
                        onClick={async () => {
                          if (!confirm(`Remove all ${poInventory.filter(i=>i.status==='incoming').length} incoming stock records from this PO? This cannot be undone.`)) return;
                          try {
                            await api.delete(`purchase-orders/${detail.id}/stock/incoming`);
                            await refreshDetail(detail.id);
                          } catch(e) { alert(e.message || 'Failed to remove stock records'); }
                        }}
                        className="px-3 py-1.5 text-[9px] font-semibold text-red-600 border border-red-200 rounded hover:bg-red-50 whitespace-nowrap"
                      >
                        Remove all incoming ({poInventory.filter(i=>i.status==='incoming').length})
                      </button>
                    )}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Asset Tag</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Name</th>
                        {/* A batch record stands in for a whole PO line, so its count is
                            the difference between one lead and five hundred of them. */}
                        <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 border-b font-bold">Qty</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Status</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Location</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">SOW</th>
                        <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold whitespace-nowrap">Exp. Delivery</th>
                        <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 border-b font-bold">Unit Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poInventory.map(item => (
                        <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 text-[10px]">
                            <button onClick={() => navigate('inventory', item.id)}
                              className="font-mono font-bold text-gold hover:underline" title="Open this stock item">
                              {item.asset_tag}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-[10px]">{item.name}</td>
                          <td className="px-3 py-2 text-[10px] text-right whitespace-nowrap">
                            {(item.quantity || 1) > 1
                              ? <span className="font-bold text-navy">{item.quantity} <span className="font-normal text-[8px] text-gray-500">units</span></span>
                              : <span className="text-gray-500">1</span>}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${
                              item.status === 'incoming' ? 'bg-amber-100 text-amber-700' :
                              item.status === 'in_stock'  ? 'bg-emerald-100 text-emerald-700' :
                              item.status === 'allocated' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {item.status === 'incoming' ? 'Incoming' : item.status === 'in_stock' ? 'In Stock' : item.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-[9px] text-gray-600">{item.location_name || '—'}</td>
                          <td className="px-3 py-2 text-[9px] text-blue-600 font-semibold">{item.allocated_sow_reference || '—'}</td>
                          <td className="px-3 py-2 text-[9px] text-gray-500 whitespace-nowrap min-w-[84px]">{item.expected_delivery ? formatDate(item.expected_delivery) : '—'}</td>
                          {/* Purchase price is copied from this PO's line, so it's
                              in the PO's currency, not necessarily sterling. */}
                          <td className="px-3 py-2 text-[10px] text-right whitespace-nowrap">{item.purchase_price ? m(item.purchase_price) : '—'}</td>
                        </tr>
                      ))}
                      {poInventory.length === 0 && (
                        <tr><td colSpan="8" className="text-center py-6 text-[10px] text-gray-400">No inventory records linked to this PO</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                  {poInventoryTotal > poInventory.length && (
                    <div className="mt-2 text-[9px] text-gray-500">
                      Showing the first {poInventory.length} of {poInventoryTotal} records.
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'notes' && (
                <div>
                  {/* Add note form */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                    <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Add Note</label>
                    <textarea
                      className="form-input resize-none w-full mb-2"
                      rows="3"
                      placeholder="Type a note, comment, or update…"
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                    />
                    <div className="mb-2">
                      <FileUpload
                        label="Attach document (optional)"
                        value={noteAttachment?.filename || null}
                        onUpload={r => setNoteAttachment(r)}
                        onClear={() => setNoteAttachment(null)}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.eml,.msg"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={submitNote}
                        disabled={savingNote || (!noteText.trim() && !noteAttachment)}
                        className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-40 hover:bg-navy/80"
                      >{savingNote ? 'Posting…' : 'Post Note'}</button>
                    </div>
                  </div>

                  {/* Notes feed */}
                  {!notesLoaded ? (
                    <div className="text-center py-6 text-[10px] text-gray-400">Loading…</div>
                  ) : poNotes.length === 0 ? (
                    <div className="text-center py-6 text-[10px] text-gray-400">No notes yet — use the form above to add the first one.</div>
                  ) : (
                    <div className="space-y-3">
                      {[...poNotes].reverse().map(n => (
                        <div key={n.id} className="bg-white border border-gray-200 rounded-lg px-3 py-2.5">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[9px] font-semibold text-navy">{n.person_name}</span>
                            <span className="text-[8px] text-gray-400">{n.created_at ? new Date(n.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                          </div>
                          {n.content && <p className="text-[10px] text-gray-700 whitespace-pre-wrap">{n.content}</p>}
                          {n.attachment_filename && (
                            <div className="mt-1.5 flex items-center gap-1.5 text-[9px] text-blue-600">
                              <span>📎</span>
                              {n.attachment_r2_key
                                ? <a href={`/api/uploads/${n.attachment_r2_key}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{n.attachment_filename}</a>
                                : <span>{n.attachment_filename}</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'quotes' && (
                <div>
                  <div className="text-[9px] text-gray-500 mb-3">Supplier quote documents attached to this PO — for auditing and reference.</div>
                  <div className="mb-4">
                    <FileUpload
                      label="Drag quote files here or click to browse"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.eml,.msg"
                      resetAfterUpload
                      onUpload={async (r) => {
                        try {
                          const saved = await api.post(`purchase-orders/${detail.id}/quotes`, { key: r.key, filename: r.filename, content_type: r.content_type, size: r.size });
                          setQuoteUploads(prev => [...prev, saved]);
                        } catch (e) {
                          alert('Failed to attach quote: ' + (e.message || 'unknown error'));
                        }
                      }}
                    />
                  </div>
                  {!quotesLoaded ? (
                    <div className="text-center py-6 text-[10px] text-gray-400">Loading…</div>
                  ) : quoteUploads.length === 0 ? (
                    <div className="text-center py-8 text-[10px] text-gray-400">No quote documents attached yet — use the upload area above.</div>
                  ) : (
                    <div className="space-y-2">
                      {quoteUploads.map(q => (
                        <div key={q.id} className="flex items-center gap-2 px-3 py-2.5 bg-white border border-gray-200 rounded-lg">
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          </svg>
                          <a href={`/api/uploads/${q.r2_key}`} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] font-semibold text-blue-600 hover:underline flex-1 truncate">{q.filename}</a>
                          <span className="text-[8px] text-gray-400 flex-shrink-0">{q.uploaded_by_name} · {q.uploaded_at ? new Date(q.uploaded_at).toLocaleDateString('en-GB') : ''}</span>
                          <button
                            onClick={async () => {
                              if (!confirm(`Remove "${q.filename}"?`)) return;
                              setDeletingQuoteId(q.id);
                              try {
                                await api.delete(`purchase-orders/${detail.id}/quotes/${q.id}`);
                                setQuoteUploads(prev => prev.filter(x => x.id !== q.id));
                              } catch (e) {
                                alert('Failed to remove: ' + (e.message || 'unknown error'));
                              } finally {
                                setDeletingQuoteId(null);
                              }
                            }}
                            disabled={deletingQuoteId === q.id}
                            className="text-gray-300 hover:text-red-500 disabled:opacity-40 flex-shrink-0"
                            title="Remove quote file"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'history' && (
                <div>
                  <div className="text-[9px] text-gray-500 mb-3">Full change log for this PO — status transitions, field edits, and inventory events.</div>
                  {poHistory.length === 0 ? (
                    <div className="text-center py-8 text-[10px] text-gray-400">No history recorded yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">When</th>
                          <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">By</th>
                          <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Action</th>
                          <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">Field</th>
                          <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">From</th>
                          <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 border-b font-bold">To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poHistory.map((h, i) => (
                          <tr key={h.id || i} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2 text-[9px] text-gray-500 whitespace-nowrap">{h.timestamp ? new Date(h.timestamp).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                            <td className="px-3 py-2 text-[9px] font-semibold">{h.person_name || h.person_id || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                h.action === 'created'   ? 'bg-emerald-100 text-emerald-700' :
                                h.action === 'po_edited' ? 'bg-amber-100 text-amber-700' :
                                h.action === 'updated' && h.field_name === 'status' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {h.action === 'po_edited' ? 'PO Edited' :
                                 h.action === 'created' ? 'Created' :
                                 h.action === 'updated' ? 'Updated' :
                                 h.action === 'inventory_created_on_acknowledge' ? 'Stock Created' :
                                 h.action === 'inventory_marked_in_stock' ? 'Stock Received' :
                                 h.action === 'inventory_created_on_delivery' ? 'Stock Created' :
                                 h.action === 'inventory_backfilled' ? 'Stock Created' :
                                 h.action === 'delivered_without_stock' ? 'Not Stocked' :
                                 h.action === 'inventory_removed_on_cancel' ? 'Stock Removed' :
                                 h.action === 'supplier_invoice_recorded' ? 'Invoice Recorded' :
                                 h.action}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-[9px] text-gray-400 font-mono">{h.field_name || '—'}</td>
                            <td className="px-3 py-2 text-[9px] text-red-500 max-w-[120px] truncate">{h.old_value || '—'}</td>
                            <td className="px-3 py-2 text-[9px] text-emerald-600 max-w-[120px] truncate">{h.new_value || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── Add Line Modal ─────────────────────────────────────────────────────── */}
      {showAddLine && detail && (
        <Modal
          title={`Add Line — ${detail.po_number}`}
          onClose={closeAddLine}
          footer={
            <>
              <button onClick={closeAddLine} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
              <button
                onClick={submitAddLine}
                disabled={!addLineForm.description.trim()}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
              >Add Line</button>
            </>
          }
        >
          {/* Catalogue prices are held in sterling, so on a foreign-currency PO
              they must not be pulled through as-is. */}
          {poCur !== 'GBP' && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 mb-3 text-[9px] text-amber-800">
              This PO is in <strong>{poCur}</strong>. Catalogue prices are shown in GBP, so enter the supplier&apos;s {poCur} price rather than accepting the pulled-through cost.
            </div>
          )}
          {/* ── Step 1: Catalogue picker (primary path) ── */}
          <div className={`rounded border p-3 mb-3 ${addLineCatItem ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[9px] font-bold text-amber-800">
                {addLineCatItem ? '✓ Linked to Product Catalogue' : '⚠ Select from Product Catalogue'}
              </div>
              {!addLineCatItem && (
                <span className="text-[8px] text-amber-600">Avoids duplication — prices &amp; codes stay consistent</span>
              )}
            </div>

            {addLineCatItem ? (
              <>
                <div className="flex items-center justify-between bg-white border border-emerald-200 rounded px-2 py-1.5">
                  <div>
                    <span className="text-[8px] font-mono font-bold text-navy mr-2">{addLineCatItem.code}</span>
                    <span className="text-[9px] font-semibold">{addLineCatItem.description}</span>
                    {addLineCatItem.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{addLineCatItem.manufacturer}</span>}
                  </div>
                  <button onClick={clearCatalogItem} className="text-[8px] text-red-400 hover:text-red-600 ml-3 flex-shrink-0">✕ Change</button>
                </div>
                {/* Before ordering more: the PMO's supply note and what is already on the shelf */}
                <div className="mt-1.5">
                  <ProductSupplyNote productId={addLineCatItem.id} canWrite={canAccess('product-notes:write')} />
                </div>
              </>
            ) : (
              <>
                <input
                  className="form-input text-[9px]"
                  placeholder="🔍 Search catalogue by name, code or manufacturer…"
                  value={addLineCatSearch}
                  onChange={e => setAddLineCatSearch(e.target.value)}
                  autoFocus
                />
                {addLineCatSearch.trim() && (
                  <div className="border border-gray-200 rounded max-h-[150px] overflow-y-auto mt-1 bg-white shadow-sm">
                    {filteredAddLineCatalog.map(c => (
                      <div
                        key={c.id}
                        onClick={() => selectCatalogItem(c)}
                        className="px-2 py-1.5 border-b border-gray-100 cursor-pointer hover:bg-gold/5 flex items-center justify-between"
                      >
                        <div>
                          <span className="text-[8px] font-mono font-bold text-navy mr-2">{c.code}</span>
                          <span className="text-[9px]">{c.description}</span>
                          {c.manufacturer_part_number && <span className="text-[8px] font-mono text-gray-500 ml-2">{c.manufacturer_part_number}</span>}
                          {c.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{c.manufacturer}</span>}
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          {c.unit_cost != null && <span className="text-[8px] font-semibold text-navy">{formatMoney(c.unit_cost)}</span>}
                          <span className="text-[8px] text-gray-400 ml-2">{c.category}</span>
                        </div>
                      </div>
                    ))}
                    {filteredAddLineCatalog.length === 0 && (
                      <div className="text-center py-3 text-[9px] text-gray-400">No catalogue matches — you can still add manually below</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Bundle & related suggestions ── */}
          {addLineCatItem && (catalogSuggestions.related.length > 0 || catalogSuggestions.bundles.length > 0) && (
            <div className="mb-3 space-y-2">
              {catalogSuggestions.bundles.length > 0 && (
                <div className="rounded border border-violet-200 bg-violet-50 p-2">
                  <div className="text-[8px] font-bold text-violet-700 mb-1.5">⊛ Bundles containing this product</div>
                  <div className="space-y-1.5">
                    {catalogSuggestions.bundles.map(bundle => (
                      <div key={bundle.id} className="bg-white border border-violet-100 rounded p-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-[9px] font-bold text-violet-800">{bundle.name}</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(bundle.items || []).map(item => (
                                <span key={item.id} className={`text-[7.5px] px-1.5 py-0.5 rounded font-medium ${item.is_optional ? 'bg-gray-100 text-gray-500' : 'bg-violet-100 text-violet-700'}`}>
                                  {item.quantity > 1 && <span className="mr-0.5">{item.quantity}×</span>}{item.code}
                                  {item.is_optional && <span className="ml-0.5 text-gray-400">(opt)</span>}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="px-2 py-1 text-[8px] font-semibold bg-violet-600 text-white rounded hover:bg-violet-700 flex-shrink-0"
                            onClick={async () => {
                              const toAdd = (bundle.items || []).filter(i => !i.is_optional && i.product_catalog_id !== addLineCatItem.id);
                              for (const item of toAdd) {
                                await addLine(detail.id, {
                                  description: item.description,
                                  part_number: item.code,
                                  quantity: item.quantity,
                                  unit_price: item.unit_cost || 0,
                                  product_catalog_id: item.product_catalog_id,
                                  line_type: 'stock',
                                });
                              }
                              if (toAdd.length > 0) viewDetail(detail.id);
                            }}
                          >Add bundle items</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {catalogSuggestions.related.length > 0 && (
                <div className="rounded border border-blue-200 bg-blue-50 p-2">
                  <div className="text-[8px] font-bold text-blue-700 mb-1.5">Often ordered with</div>
                  <div className="flex flex-wrap gap-1.5">
                    {catalogSuggestions.related.map(r => (
                      <button
                        key={r.id}
                        type="button"
                        className="flex items-center gap-1.5 px-2 py-1 bg-white border border-blue-200 rounded text-[8px] hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        onClick={() => selectCatalogItem(r)}
                      >
                        <span className="font-mono font-bold text-navy text-[7.5px]">{r.code}</span>
                        <span className="text-gray-700 truncate max-w-[140px]">{r.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Line details (auto-filled or manual) ── */}
          {!addLineCatItem && (
            <div className="text-[8px] text-gray-400 mb-2 border-t border-gray-100 pt-2">
              Or add manually — only use this for items not yet in the catalogue:
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {/* Line type toggle */}
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Line Type</label>
              <div className="flex gap-2">
                {['stock', 'service'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setALF('line_type', t)}
                    className={`px-3 py-1 text-[9px] font-semibold rounded border transition-colors ${addLineForm.line_type === t ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                  >{t === 'stock' ? '📦 Physical Goods (Stock)' : '🔧 Third-Party Service'}</button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description *</label>
              <input className="form-input" placeholder={addLineForm.line_type === 'service' ? 'e.g. Installation & commissioning' : 'e.g. Cisco Switch 48-port'} value={addLineForm.description} onChange={e => setALF('description', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Product Code</label>
              <input className="form-input font-mono text-[9px]" placeholder="e.g. WS-C3850-48T" value={addLineForm.part_number} onChange={e => setALF('part_number', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quantity</label>
              <input className="form-input" type="number" min="1" value={addLineForm.quantity} onChange={e => setALF('quantity', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Unit Price (excl. VAT) {poSym}{poCur !== 'GBP' ? ` ${poCur}` : ''}</label>
              <input className="form-input" type="number" step="0.01" placeholder="0.00" value={addLineForm.unit_price} onChange={e => setALF('unit_price', e.target.value)} />
            </div>
            {addLineForm.unit_price && addLineForm.quantity && (
              <div className="col-span-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-[9px] text-gray-600">
                Line total: <span className="font-bold text-navy">{m((parseFloat(addLineForm.unit_price) || 0) * (parseInt(addLineForm.quantity) || 1))}</span>
              </div>
            )}
            {/* Service-specific fields */}
            {addLineForm.line_type === 'service' && (
              <>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Service Status</label>
                  <select className="form-select" value={addLineForm.service_status} onChange={e => setALF('service_status', e.target.value)}>
                    <option value="outstanding">Outstanding</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Forecast Completion Date</label>
                  <input className="form-input" type="date" value={addLineForm.service_forecast_date} onChange={e => setALF('service_forecast_date', e.target.value)} />
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* ── Transition modals ──────────────────────────────────────────────────── */}

      {/* Cancel PO — requires a reason */}
      {transitionModal === 'cancel' && (
        <Modal title="Cancel Purchase Order" onClose={closeTransitionModal} footer={
          <>
            <button onClick={closeTransitionModal} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Back</button>
            <button
              onClick={commitTransition}
              disabled={!cancelReason.trim() || transitioning}
              className="px-3 py-1 text-[10px] font-semibold bg-red-600 text-white rounded disabled:opacity-40"
            >{transitioning ? 'Cancelling…' : 'Confirm Cancellation'}</button>
          </>
        }>
          <p className="text-[9px] text-gray-600 mb-3">
            You are about to cancel <strong>{detail?.po_number}</strong>.
            Any inventory records with status <em>Stock Incoming</em> from this PO will be removed.
          </p>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Reason *</label>
          <textarea
            className="form-input w-full h-20 resize-none"
            placeholder="e.g. Supplier unable to fulfil, alternative source found…"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            autoFocus
          />
          {transitionError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{transitionError}</div>}
        </Modal>
      )}

      {/* Acknowledge — captures expected delivery date (global + per-line) and creates inventory */}
      {transitionModal === 'acknowledge' && (
        <Modal title="Acknowledge Purchase Order" onClose={closeTransitionModal} wide footer={
          <>
            <button onClick={closeTransitionModal} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Back</button>
            <button
              onClick={() => {
                // A record per unit at this size is worth pausing on: it is the click
                // that put 3,379 asset-tagged sticky dots in the register.
                if (needsIndividualConfirm(inventoryMode, detail?.lines || [])) {
                  setTransitionModal('acknowledge-confirm');
                  return;
                }
                commitTransition();
              }}
              disabled={transitioning}
              className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
            >{transitioning ? 'Acknowledging…' : 'Acknowledge PO'}</button>
          </>
        }>
          {/* Inventory creation mode */}
          <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
              <span className="text-[8px] font-bold uppercase text-gray-500">Inventory Record Creation</span>
              {needsIndividualConfirm('individual', detail?.lines || []) && (
                <span className="ml-2 text-[8px] text-gray-500">
                  — {totalUnits(detail?.lines || [])} units ordered, so a batch per line is preselected
                </span>
              )}
            </div>
            <div className="divide-y divide-gray-100">
              {[
                { value: 'individual', label: 'Create individually', desc: `One stock record per unit — e.g. ${(detail?.lines||[]).reduce((s,l)=>s+(parseInt(l.quantity)||0),0)} records total. Best for serialised assets (laptops, servers, handsets).` },
                { value: 'batch', label: 'Create as single batch per line', desc: `One stock record per PO line (${(detail?.lines||[]).length} record${(detail?.lines||[]).length!==1?'s':''}). Best for consumables ordered in bulk where individual tracking isn\'t needed.` },
                { value: 'none', label: 'Don\'t create inventory records', desc: 'No stock records created. Use when items are services, software licences, or will be managed entirely outside the inventory register.' },
              ].map(opt => (
                <label key={opt.value} className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${inventoryMode === opt.value ? 'bg-navy/5' : ''}`}>
                  <input type="radio" name="inv_mode" value={opt.value} checked={inventoryMode === opt.value} onChange={() => setInventoryMode(opt.value)} className="mt-0.5 accent-navy" />
                  <div>
                    <div className="text-[9px] font-semibold text-gray-800">{opt.label}</div>
                    <div className="text-[8px] text-gray-500 mt-0.5">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            {/* Goods raised from a hardware request are physical kit somebody is
                waiting for. Said here, where the choice is made, because the
                consequence only shows days later when the goods arrive. */}
            {inventoryMode === 'none' && (detail?.lines || []).some(l => l.hardware_request_id) && (
              <div className="px-3 py-2 border-t border-amber-200 bg-amber-50 text-[8px] text-amber-800">
                {(detail?.lines || []).filter(l => l.hardware_request_id).map(l => l.description).join(', ')}
                {(detail?.lines || []).filter(l => l.hardware_request_id).length === 1 ? ' was' : ' were'} raised from a hardware request.
                Without a stock record nothing reaches Inventory when it arrives, so you will be asked about it again when the PO is marked delivered.
              </div>
            )}
          </div>

          {/* Global fallback date */}
          <div className="mb-4">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">
              Default Delivery Date <span className="font-normal text-gray-400">(applies to all lines unless overridden below)</span>
            </label>
            <input
              type="date"
              className="form-input"
              value={expectedDelivDate}
              onChange={e => {
                setExpectedDelivDate(e.target.value);
                // Apply global date to any line that doesn't already have a per-line override
                const updated = { ...lineDelivDates };
                (detail?.lines || []).forEach(l => { if (!updated[l.id]) updated[l.id] = e.target.value; });
                setLineDelivDates(updated);
              }}
              autoFocus
            />
          </div>

          {/* Per-line delivery dates */}
          {(detail?.lines || []).length > 0 && (
            <div className="border border-gray-200 rounded overflow-hidden">
              <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                <span className="text-[8px] font-bold uppercase text-gray-500">Per-Line Delivery Dates</span>
                <span className="text-[8px] text-gray-400 ml-2">Override the date for individual lines if delivery is split</span>
              </div>
              <div className="overflow-x-auto"><table className="w-full">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-3 py-1.5 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Line</th>
                    <th className="px-3 py-1.5 text-center text-[7px] uppercase text-gray-400 font-bold border-b">Qty</th>
                    <th className="px-3 py-1.5 text-left text-[7px] uppercase text-gray-400 font-bold border-b">Expected Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail?.lines || []).map((l, idx) => (
                    <tr key={l.id} className="border-b border-gray-100">
                      <td className="px-3 py-2">
                        <div className="text-[9px] font-medium text-gray-800">{l.description}</div>
                        {l.part_number && <div className="text-[8px] text-gray-400 font-mono">{l.part_number}</div>}
                      </td>
                      <td className="px-3 py-2 text-center text-[9px] text-gray-600 font-semibold">{l.quantity}</td>
                      <td className="px-3 py-2">
                        <input
                          type="date"
                          className="form-input text-[9px] py-0.5"
                          value={lineDelivDates[l.id] || ''}
                          onChange={e => setLineDelivDates(p => ({ ...p, [l.id]: e.target.value }))}
                          placeholder={expectedDelivDate || 'No date set'}
                        />
                        {lineDelivDates[l.id] && lineDelivDates[l.id] !== expectedDelivDate && (
                          <div className="text-[7px] text-amber-600 mt-0.5">Split delivery date</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}
          {/* Optional acknowledgement comment */}
          <div className="mt-4">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">
              Acknowledgement Comment <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              className="form-textarea w-full text-[9px]"
              rows="2"
              placeholder="Any notes about the acknowledgement, e.g. delivery terms agreed, lead time confirmed…"
              value={acknowledgeComment}
              onChange={e => setAcknowledgeComment(e.target.value)}
            />
          </div>
          {transitionError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{transitionError}</div>}
        </Modal>
      )}

      {/* Confirm a record per unit on a large order. Rendered instead of the acknowledge
          modal rather than on top of it: two stacked modals both listen for Escape, so
          one keypress would dismiss the confirm and the form behind it together. Going
          Back returns to the form with every answer still filled in. */}
      {transitionModal === 'acknowledge-confirm' && (
        <Modal title="Create one stock record per unit?" onClose={closeTransitionModal} footer={
          <>
            <button onClick={() => setTransitionModal('acknowledge')} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Back</button>
            <button
              onClick={() => { setInventoryMode('batch'); commitTransition('batch'); }}
              disabled={transitioning}
              className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
            >{transitioning ? 'Acknowledging…' : 'Use one batch per line'}</button>
            <button
              onClick={() => commitTransition('individual')}
              disabled={transitioning}
              className="px-3 py-1 text-[10px] font-semibold text-amber-900 bg-amber-100 border border-amber-300 rounded disabled:opacity-40"
            >{transitioning ? 'Acknowledging…' : `Yes, create ${totalUnits(detail?.lines || [])} records`}</button>
          </>
        }>
          <p className="text-[9px] text-gray-600">
            Acknowledging <strong>{detail?.po_number}</strong> this way creates{' '}
            <strong>{totalUnits(detail?.lines || [])} separate stock records</strong>, each with its own
            asset tag.
          </p>
          <p className="text-[9px] text-gray-600 mt-2">
            That is what you want for serialised kit, where every unit has its own warranty and
            history. For consumables it fills the register with thousands of rows that nobody
            tracks individually, and they cannot be merged back afterwards without deleting them.
          </p>
          <p className="text-[9px] text-gray-600 mt-2">
            A single batch record per line keeps the quantity without the asset tags.
          </p>
          {transitionError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{transitionError}</div>}
        </Modal>
      )}

      {/* Generic confirm for other transitions. Marking delivered with lines the
          register has no record of changes the dialog: it names them and the
          primary button puts the ticked ones into stock as the PO is delivered,
          rather than confirming a delivery that leaves Inventory empty. */}
      {transitionModal === 'confirm' && (
        <Modal
          title={transitionUnstocked.length > 0
            ? `Add to stock and mark ${STATUS_LABELS[transitionTarget]}?`
            : `Move to: ${STATUS_LABELS[transitionTarget]}`}
          onClose={closeTransitionModal}
          wide={transitionUnstocked.length > 0}
          footer={
          <>
            <button onClick={closeTransitionModal} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Back</button>
            <button
              onClick={commitTransition}
              disabled={transitioning}
              className={`px-3 py-1 text-[10px] font-semibold text-white rounded disabled:opacity-40 ${transitionUnstocked.length > 0 && stockTicked > 0 ? 'bg-emerald-600' : 'bg-navy'}`}
            >{transitioning ? 'Updating…'
              : transitionUnstocked.length === 0 ? `Confirm — ${STATUS_LABELS[transitionTarget]}`
              : stockTicked > 0 ? `Add ${stockTicked} line${stockTicked !== 1 ? 's' : ''} to stock and mark ${STATUS_LABELS[transitionTarget]}`
              : `Mark ${STATUS_LABELS[transitionTarget]} without stock`}</button>
          </>
        }>
          {transitionUnstocked.length > 0 ? (
            <div>
              <p className="text-[9px] text-gray-600 mb-2">
                {transitionUnstocked.length === 1
                  ? <><strong>{transitionUnstocked[0].description}</strong> has</>
                  : <>{transitionUnstocked.length} lines on <strong>{detail?.po_number}</strong> have</>} no stock record,
                so marking it {STATUS_LABELS[transitionTarget]} as it stands puts nothing into Inventory. Records are made
                when a PO is acknowledged, and this one was acknowledged with <em>Don't create inventory records</em> or
                had lines added afterwards.
              </p>
              <p className="text-[9px] text-gray-600 mb-3">
                Tick what arrived as goods. Leave delivery charges and services unticked.
              </p>
              <MissingStockPicker
                lines={transitionUnstocked}
                selected={stockLineIds}
                onToggle={toggleStockLine}
                mode={stockMode}
                onMode={setStockMode}
              />
            </div>
          ) : (
            <p className="text-[9px] text-gray-600">
              Move <strong>{detail?.po_number}</strong> to <strong>{STATUS_LABELS[transitionTarget]}</strong>?
              {transitionReceivesGoods && incomingItems.length > 0 && (
                <span className="block mt-2 text-emerald-700 font-semibold">
                  ✓ {incomingItems.length} incoming inventory item{incomingItems.length !== 1 ? 's' : ''} will be moved to In Stock.
                </span>
              )}
            </p>
          )}
          {/* Where the goods are going, whenever this step receives any. */}
          {transitionReceivesGoods && (incomingItems.length > 0 || stockTicked > 0) && (
            <div className="mt-3">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Storage Location</label>
              <select className="form-select" value={receiveLocation} onChange={e => setReceiveLocation(e.target.value)}>
                <option value="">— Unassigned —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          {transitionError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{transitionError}</div>}
        </Modal>
      )}

      {/* Add to Stock: a PO marked delivered before its lines had stock records.
          The goods arrived, so what is created here goes straight into stock. */}
      {showAddToStock && detail && (
        <Modal
          title={`Add to Stock — ${detail.po_number}`}
          onClose={() => setShowAddToStock(false)}
          wide
          footer={
            <>
              <button onClick={() => setShowAddToStock(false)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
              <button
                onClick={submitAddToStock}
                disabled={addingToStock || missingStockLines.filter(l => stockLineIds.has(l.id)).length === 0}
                className="px-3 py-1 text-[10px] font-semibold bg-emerald-600 text-white rounded disabled:opacity-40"
              >{addingToStock ? 'Adding…' : `Add ${missingStockLines.filter(l => stockLineIds.has(l.id)).length} line${missingStockLines.filter(l => stockLineIds.has(l.id)).length !== 1 ? 's' : ''} to stock`}</button>
            </>
          }
        >
          <p className="text-[9px] text-gray-600 mb-3">
            <strong>{detail.po_number}</strong> is {STATUS_LABELS[detail.status]}, but{' '}
            {missingStockLines.length === 1 ? 'one line has' : `${missingStockLines.length} lines have`} no stock record,
            so the goods never reached Inventory. Tick what arrived as goods and it is added as received,
            allocated to its SOW where the request named one.
          </p>
          <MissingStockPicker
            lines={missingStockLines}
            selected={stockLineIds}
            onToggle={toggleStockLine}
            mode={stockMode}
            onMode={setStockMode}
          />
          <div className="mt-3">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Storage Location</label>
            <select className="form-select" value={receiveLocation} onChange={e => setReceiveLocation(e.target.value)}>
              <option value="">— Unassigned —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          {addToStockError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{addToStockError}</div>}
        </Modal>
      )}

      {/* Notify Requester Modal — the PO is delivered, but nothing on it told us
          who to tell. Optional: skipping leaves the PO delivered and unnotified. */}
      {notifyRequester && (
        <Modal
          title="Notify Requester"
          onClose={() => setNotifyRequester(null)}
          footer={
            <>
              <button
                onClick={() => setNotifyRequester(null)}
                className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white"
              >Skip</button>
              <button
                onClick={submitNotifyRequester}
                disabled={notifySending || !notifyForm.email.trim()}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
              >{notifySending ? 'Sending…' : 'Send Notification'}</button>
            </>
          }
        >
          <p className="text-[9px] text-gray-600 mb-3">
            <strong>{notifyRequester.po_number}</strong> is marked as delivered, but there is no requester
            on record to notify. Add an address to send the delivery notification, or skip.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Requester Name</label>
              <input
                className="form-input"
                placeholder="e.g. Alex Johnson"
                value={notifyForm.name}
                onChange={e => setNotifyForm(p => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Email *</label>
              <input
                className="form-input"
                type="email"
                placeholder="name@altonvalley.com"
                value={notifyForm.email}
                onChange={e => setNotifyForm(p => ({ ...p, email: e.target.value }))}
                autoFocus
              />
            </div>
          </div>
          <p className="mt-2 text-[8px] text-gray-400">
            Saved against the PO, so a later delivery on it won't ask again.
          </p>
          {notifyError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{notifyError}</div>}
        </Modal>
      )}

      {/* Record Supplier Invoice Modal */}
      {showRecordInvoice && (
        <Modal
          title="Record Supplier Invoice"
          onClose={() => setShowRecordInvoice(false)}
          footer={
            <>
              <button onClick={() => setShowRecordInvoice(false)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
              <button
                onClick={submitSupplierInvoice}
                disabled={!supplierInvoiceForm.invoice_number.trim() || !supplierInvoiceForm.invoice_date}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
              >Save Invoice</button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Number *</label>
              <input className="form-input" placeholder="e.g. SUP-INV-12345" value={supplierInvoiceForm.invoice_number} onChange={e => setSIF('invoice_number', e.target.value)} autoFocus />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Date *</label>
              <input className="form-input" type="date" value={supplierInvoiceForm.invoice_date} onChange={e => setSIF('invoice_date', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment Date</label>
              <input className="form-input" type="date" value={supplierInvoiceForm.payment_date} onChange={e => setSIF('payment_date', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Amount (excl. VAT) {poSym}{poCur !== 'GBP' ? ` ${poCur}` : ''}</label>
              <input className="form-input" type="number" step="0.01" placeholder="0.00" value={supplierInvoiceForm.amount_excl_vat} onChange={e => setSIF('amount_excl_vat', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Amount (incl. VAT) {poSym}{poCur !== 'GBP' ? ` ${poCur}` : ''}</label>
              <input className="form-input" type="number" step="0.01" placeholder="0.00" value={supplierInvoiceForm.amount_incl_vat} onChange={e => setSIF('amount_incl_vat', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
              <input className="form-input" placeholder="Optional notes..." value={supplierInvoiceForm.notes} onChange={e => setSIF('notes', e.target.value)} />
            </div>
          </div>
          {supplierInvoiceForm.amount_excl_vat && remaining !== null && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-[9px] text-blue-700">
              After recording: <span className="font-bold">{m(remaining - (parseFloat(supplierInvoiceForm.amount_excl_vat) || 0))}</span> remaining on PO
            </div>
          )}
        </Modal>
      )}

      {/* Edit PO Modal — draft (correct creation details) or any open non-closed PO */}
      {showEditPO && detail && (
        <EditPOModal
          po={detail}
          suppliers={suppliers}
          sows={sows}
          addresses={addresses}
          onClose={() => setShowEditPO(false)}
          onSave={async (updates) => {
            await api.put(`purchase-orders/${detail.id}`, updates);
            setShowEditPO(false);
            load();
            await refreshDetail(detail.id);
          }}
        />
      )}

      {/* Record Delivery Modal */}
      {showDelivery && detail && (
        <Modal
          title={`Record Delivery — ${detail.po_number}`}
          onClose={() => setShowDelivery(false)}
          wide
          footer={
            <>
              <button onClick={() => setShowDelivery(false)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
              {needsStockRecords ? (
                <button
                  onClick={createMissingStock}
                  disabled={creatingStock}
                  className="px-3 py-1 text-[10px] font-semibold bg-emerald-600 text-white rounded disabled:opacity-40"
                >{creatingStock ? 'Creating…' : `Create stock records for ${missingStockLines.length} line${missingStockLines.length !== 1 ? 's' : ''}`}</button>
              ) : (
                <button
                  onClick={submitDelivery}
                  disabled={submittingDelivery || selectedItems.size === 0 || !deliveryDate || !!deliveryQtyError}
                  className="px-3 py-1 text-[10px] font-semibold bg-emerald-600 text-white rounded disabled:opacity-40"
                >{submittingDelivery ? 'Recording…' : `Record ${unitsSelected} Unit${unitsSelected !== 1 ? 's' : ''} Received`}</button>
              )}
            </>
          }
        >
          {/* Nothing to receive because the PO has no stock records. Say what is in
              the way and make the button create them, rather than presenting an
              empty list and leaving somebody to work out why. */}
          {needsStockRecords ? (
            <div>
              <p className="text-[9px] text-gray-600 mb-3">
                <strong>{detail.po_number}</strong> has no stock records, so there is nothing to tick off as delivered.
                Records are created when a PO is acknowledged, and this one was either acknowledged with
                <em> Don't create inventory records</em>, or had its lines added afterwards.
                Creating them now puts {missingStockLines.length} outstanding line{missingStockLines.length !== 1 ? 's' : ''} into
                the register as <em>Stock Incoming</em>, exactly as acknowledging would have done, and the delivery can then be recorded here.
              </p>

              <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                  <span className="text-[8px] font-bold uppercase text-gray-500">Inventory Record Creation</span>
                  {needsIndividualConfirm('individual', missingStockLines) && (
                    <span className="ml-2 text-[8px] text-gray-500">
                      — {totalUnits(missingStockLines)} units outstanding, so a batch per line is preselected
                    </span>
                  )}
                </div>
                <div className="divide-y divide-gray-100">
                  {[
                    { value: 'batch', label: 'Create as single batch per line', desc: `One stock record per line (${missingStockLines.length} record${missingStockLines.length !== 1 ? 's' : ''}), carrying the quantity ordered. Best for consumables and anything not tracked unit by unit.` },
                    { value: 'individual', label: 'Create individually', desc: `One stock record per unit — ${totalUnits(missingStockLines)} record${totalUnits(missingStockLines) !== 1 ? 's' : ''} total. Best for serialised assets (laptops, servers, handsets).` },
                  ].map(opt => (
                    <label key={opt.value} className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${backfillMode === opt.value ? 'bg-navy/5' : ''}`}>
                      <input type="radio" name="backfill_mode" value={opt.value} checked={backfillMode === opt.value} onChange={() => setBackfillMode(opt.value)} className="mt-0.5 accent-navy" />
                      <div>
                        <div className="text-[9px] font-semibold text-gray-800">{opt.label}</div>
                        <div className="text-[8px] text-gray-500 mt-0.5">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 rounded overflow-hidden">
                <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                  <span className="text-[8px] font-bold uppercase text-gray-500">Lines with no stock record</span>
                </div>
                <div className="max-h-[220px] overflow-y-auto overflow-x-auto">
                  <table className="w-full">
                    <tbody>
                      {missingStockLines.map(line => (
                        <tr key={line.id} className="border-b border-gray-100">
                          <td className="px-3 py-2 text-[10px]">{line.description}</td>
                          <td className="px-3 py-2 text-[9px] font-mono text-gray-400">{line.part_number || ''}</td>
                          <td className="px-3 py-2 text-[9px] text-gray-500 text-right w-16">Qty {line.quantity ?? 1}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {backfillError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{backfillError}</div>}
            </div>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Actual Delivery Date *</label>
              <input className="form-input" type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} autoFocus />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Storage Location</label>
              <select className="form-select" value={deliveryLocation} onChange={e => setDeliveryLocation(e.target.value)}>
                <option value="">— Unassigned —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes (optional)</label>
              <input className="form-input" placeholder="e.g. Partial delivery — awaiting remaining items" value={deliveryNotes} onChange={e => setDeliveryNotes(e.target.value)} />
            </div>
          </div>

          <div className="border border-gray-200 rounded overflow-hidden">
            <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200 flex items-center justify-between">
              <span className="text-[8px] font-bold uppercase text-gray-500">
                Select Items Received — {poInventory.filter(i => i.status === 'incoming').length} incoming
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedItems(new Set(poInventory.filter(i => i.status === 'incoming').map(i => i.id)))}
                  className="text-[8px] text-gold hover:underline"
                >Select All</button>
                <button onClick={() => setSelectedItems(new Set())} className="text-[8px] text-gray-400 hover:underline">Clear</button>
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
              <table className="w-full">
                <tbody>
                  {poInventory.filter(i => i.status === 'incoming').map(item => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedItems(prev => {
                        const next = new Set(prev);
                        if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                        return next;
                      })}
                    >
                      <td className="px-3 py-2 w-8">
                        <input type="checkbox" readOnly checked={selectedItems.has(item.id)} className="pointer-events-none" />
                      </td>
                      <td className="px-3 py-2 text-[10px] font-mono font-bold text-gold w-24">{item.asset_tag}</td>
                      <td className="px-3 py-2 text-[10px]">{item.name}</td>
                      <td className="px-3 py-2 text-[9px] text-gray-500 whitespace-nowrap" onClick={e => { if (selectedItems.has(item.id)) e.stopPropagation(); }}>
                        {unitsHeld(item) > 1 && (selectedItems.has(item.id) ? (
                          <span className="inline-flex items-center gap-1">
                            <input
                              type="number" min="1" max={unitsHeld(item)} step="1"
                              aria-label={`Units of ${item.asset_tag} received`}
                              className="form-input !w-16 !py-0.5 !text-[10px] text-right"
                              value={receivedQty[item.id] ?? unitsHeld(item)}
                              onChange={e => setReceivedQty(prev => ({ ...prev, [item.id]: e.target.value === '' ? '' : Number(e.target.value) }))}
                            />
                            of {unitsHeld(item)} units
                          </span>
                        ) : `${unitsHeld(item)} units`)}
                      </td>
                      <td className="px-3 py-2 text-[9px] text-gray-400">{item.expected_delivery ? `ETA ${formatDate(item.expected_delivery)}` : ''}</td>
                      <td className="px-3 py-2 text-[9px] text-blue-600 font-semibold">{item.allocated_sow_reference || ''}</td>
                    </tr>
                  ))}
                  {poInventory.filter(i => i.status === 'incoming').length === 0 && (
                    <tr><td colSpan="6" className="text-center py-6 text-[10px] text-gray-400">No incoming items to record</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {selectedItems.size > 0 && !deliveryQtyError && (
            <div className="mt-2 text-[9px] text-emerald-700 font-semibold">
              {unitsSelected} unit{unitsSelected !== 1 ? 's' : ''} will be marked as {
                poInventory.filter(i => selectedItems.has(i.id) && i.allocated_to_sow_id).length > 0 ? 'Allocated' : 'In Stock'
              } and given received date {deliveryDate || '(set date above)'}.
              {partReceived.map(i => (
                <div key={i.id} className="font-normal text-gray-600 mt-0.5">
                  {i.asset_tag}: {unitsArrived(i.id)} of {unitsHeld(i)} received as a new record. The other {unitsHeld(i) - unitsArrived(i.id)} stay Incoming on {i.asset_tag}.
                </div>
              ))}
            </div>
          )}
          {deliveryQtyError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{deliveryQtyError}</div>}
          {deliveryError && <div className="mt-2 text-[9px] text-red-600 font-semibold">{deliveryError}</div>}
          </>
          )}
        </Modal>
      )}

      {/* Create PO Modal */}
      {showCreate && (
        <CreatePOModal
          suppliers={suppliers}
          sows={sows}
          addresses={addresses}
          onClose={() => setShowCreate(false)}
          onSave={createPO}
        />
      )}

      {/* Manage Delivery Addresses Modal */}
      {showManageAddresses && (
        <DeliveryAddressModal
          onClose={() => { setShowManageAddresses(false); load(); }}
        />
      )}

    </div>
  );
}

function CreatePOModal({ suppliers, sows, addresses, onClose, onSave }) {
  const [form, setForm] = useState({ supplier_id: '', sow_id: '', delivery_address_id: '', delivery_attention: '', notes: '', specific_terms: '', order_date: new Date().toISOString().split('T')[0], requester_name: '', quote_reference: '', currency: 'GBP', qa_override_reason: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [submitting, setSubmitting] = useState(false);
  const [gate, setGate]   = useState(null);   // backend qa_gate payload (fallback if list data is stale)
  const [error, setError] = useState('');
  const [quoteFiles, setQuoteFiles] = useState([]); // { key, filename, content_type, size }

  // Supplier QA soft-gate: a PO can be raised against a non-compliant supplier,
  // but only with a recorded acknowledgement reason.
  const sel = suppliers.find(s => s.id === form.supplier_id);
  const qaReq = sel?.qa_required || 0, qaVer = sel?.qa_verified || 0, qaExp = sel?.qa_expired || 0;
  const nonCompliant = !!sel && qaReq > 0 && (qaVer < qaReq || qaExp > 0);
  const gated = nonCompliant || !!gate;
  const needReason = gated && !form.qa_override_reason.trim();

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      await onSave({ ...form, quote_files: quoteFiles });
    } catch (e) {
      if (e.data?.qa_gate) setGate(e.data);
      else setError(e.data?.error || e.message || 'Failed to create PO');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="New Purchase Order"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
          <button
            onClick={submit}
            disabled={!form.supplier_id || needReason || submitting}
            className={`px-3 py-1 text-[10px] font-semibold text-white rounded disabled:opacity-40 ${gated ? 'bg-amber-600' : 'bg-gold'}`}
          >{submitting ? 'Creating…' : gated ? 'Raise PO anyway' : 'Create PO'}</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Supplier *</label>
          <select className="form-select" value={form.supplier_id} onChange={e => set('supplier_id', e.target.value)}>
            <option value="">Select...</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Order Date</label>
          <input className="form-input" type="date" value={form.order_date} onChange={e => set('order_date', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">SOW (optional)</label>
          <SowSelect sows={sows} value={form.sow_id} onChange={id => set('sow_id', id)} placeholder="None" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Delivery Address</label>
          <select className="form-select" value={form.delivery_address_id} onChange={e => set('delivery_address_id', e.target.value)}>
            <option value="">Select...</option>
            {addresses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {(() => {
            const a = addresses.find(x => String(x.id) === String(form.delivery_address_id));
            if (!a) return null;
            const lines = [a.company_name, a.address_line1, a.address_line2, a.address_line3, a.city, a.county, a.postcode].filter(Boolean).join('\n');
            return (
              <div className="mt-1 text-[9px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1 whitespace-pre-line">
                <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">PO ships to</div>
                {lines}
                <div className="text-[7px] text-gray-400 mt-0.5">The label “{a.name}” is internal only and isn’t printed on the PO.</div>
              </div>
            );
          })()}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Deliver to / Attention <span className="font-normal text-gray-400">(name — printed as “FAO:” on the PO)</span></label>
          <input className="form-input" placeholder="e.g. Olivia Basile / IT Stores" value={form.delivery_attention} onChange={e => set('delivery_attention', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Currency</label>
          <select className="form-select" value={form.currency} onChange={e => set('currency', e.target.value)}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c} {currencySymbol(c)}</option>)}
          </select>
          {form.currency !== 'GBP' && (
            <div className="mt-1 text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Line prices are entered and printed in {form.currency}. Nothing is converted, so this PO is excluded from the sterling spend reporting.
            </div>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Requested By <span className="font-normal text-gray-400">(if different from you — e.g. raised on behalf of)</span></label>
          <input className="form-input" placeholder="e.g. John Smith" value={form.requester_name} onChange={e => set('requester_name', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quote Reference <span className="font-normal text-gray-400">(supplier quote / quotation number)</span></label>
          <input className="form-input" placeholder="e.g. QT-2024-00123" value={form.quote_reference} onChange={e => set('quote_reference', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quote Documents <span className="font-normal text-gray-400">(attach supplier quote files — PDF, image, etc.)</span></label>
          {quoteFiles.length > 0 && (
            <div className="mb-2 space-y-1">
              {quoteFiles.map((f, i) => (
                <div key={f.key} className="flex items-center gap-2 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span className="text-[9px] font-semibold text-emerald-700 flex-1 truncate">{f.filename}</span>
                  <button type="button" onClick={() => setQuoteFiles(prev => prev.filter((_, idx) => idx !== i))}
                    className="text-emerald-400 hover:text-red-500 font-bold text-[11px] leading-none flex-shrink-0">×</button>
                </div>
              ))}
            </div>
          )}
          <FileUpload
            label="Drag quote files here or click to browse"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.eml,.msg"
            resetAfterUpload
            onUpload={r => setQuoteFiles(prev => [...prev, { key: r.key, filename: r.filename, content_type: r.content_type, size: r.size }])}
          />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes / Description</label>
          <textarea className="form-textarea" rows="2" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Specific PO Terms and Conditions</label>
          <textarea className="form-textarea" rows="3" placeholder="Enter any specific terms for this PO — these will appear on the printed document." value={form.specific_terms} onChange={e => set('specific_terms', e.target.value)} />
        </div>
      </div>
      {/* Supplier QA soft-gate */}
      {gated && (
        <div className="mt-2 p-2.5 bg-amber-50 border border-amber-300 rounded">
          <div className="text-[10px] font-bold text-amber-800">⚠ Supplier QA incomplete</div>
          <div className="text-[9px] text-amber-700 mt-0.5">
            {gate?.outstanding?.length
              ? <>Outstanding: {gate.outstanding.join(', ')}.</>
              : <>{qaVer}/{qaReq} required QA steps verified{qaExp > 0 ? `, ${qaExp} expired` : ''}.</>}
            {' '}You can still raise this PO, but you must record why.
          </div>
          <label className="block text-[8px] font-bold text-amber-700 uppercase mt-2 mb-0.5">Acknowledgement reason *</label>
          <textarea className="form-textarea" rows="2" placeholder="e.g. Urgent requirement; insurance renewal in progress and being chased." value={form.qa_override_reason} onChange={e => set('qa_override_reason', e.target.value)} />
        </div>
      )}
      {!gated && sel && qaReq > 0 && (
        <div className="mt-2 text-[9px] text-green-700">✓ Supplier QA compliant ({qaVer}/{qaReq} required steps verified).</div>
      )}
      {error && <div className="mt-2 text-[9px] text-red-600">{error}</div>}

      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-[9px] text-blue-600">
        A sequential PO number (AV-PO-XXXXXX) will be assigned automatically.
      </div>
    </Modal>
  );
}

function EditPOModal({ po, suppliers, sows, addresses, onClose, onSave }) {
  const isDraft = po.status === 'draft';
  const [form, setForm] = useState({
    supplier_id:         po.supplier_id        || '',
    sow_id:              po.sow_id             || '',
    delivery_address_id: po.delivery_address_id || '',
    delivery_attention:  po.delivery_attention || '',
    notes:               po.notes              || '',
    specific_terms:      po.specific_terms     || '',
    order_date:          po.order_date         || '',
    expected_delivery:   po.expected_delivery  || '',
    requester_name:      po.requester_name     || '',
    quote_reference:     po.quote_reference    || '',
    currency:            (po.currency || 'GBP').toUpperCase(),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setError('');
    try { await onSave(form); }
    catch (e) { setError(e.message || 'Save failed'); setSaving(false); }
  };

  return (
    <Modal
      title={`Edit PO — ${po.po_number}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }
    >
      {isDraft ? (
        <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-[9px] text-gray-600">
          Draft PO — correct any details entered at creation. Changes save immediately; no revision is recorded until the PO is issued.
        </div>
      ) : (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-[9px] text-amber-700">
          This PO has already been issued. Changes will be tracked and a revision number will be recorded.
          Re-issue the PO PDF after saving to send the updated version to the supplier.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Supplier</label>
          <select className="form-select" value={form.supplier_id} onChange={e => set('supplier_id', e.target.value)}>
            <option value="">Select…</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Expected Delivery</label>
          <input className="form-input" type="date" value={form.expected_delivery} onChange={e => set('expected_delivery', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">SOW (optional)</label>
          <SowSelect sows={sows} value={form.sow_id} onChange={id => set('sow_id', id)} placeholder="None" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Delivery Address</label>
          <select className="form-select" value={form.delivery_address_id} onChange={e => set('delivery_address_id', e.target.value)}>
            <option value="">Select…</option>
            {addresses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {(() => {
            const a = addresses.find(x => String(x.id) === String(form.delivery_address_id));
            if (!a) return null;
            const lines = [a.company_name, a.address_line1, a.address_line2, a.address_line3, a.city, a.county, a.postcode].filter(Boolean).join('\n');
            return (
              <div className="mt-1 text-[9px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1 whitespace-pre-line">
                <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">PO ships to</div>
                {lines}
                <div className="text-[7px] text-gray-400 mt-0.5">The label “{a.name}” is internal only and isn’t printed on the PO.</div>
              </div>
            );
          })()}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Deliver to / Attention <span className="font-normal text-gray-400">(name — “FAO:”)</span></label>
          <input className="form-input" placeholder="e.g. Olivia Basile / IT Stores" value={form.delivery_attention} onChange={e => set('delivery_attention', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Order Date</label>
          <input className="form-input" type="date" value={form.order_date} onChange={e => set('order_date', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Requester</label>
          <input className="form-input" placeholder="Who requested it" value={form.requester_name} onChange={e => set('requester_name', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Currency</label>
          <select className="form-select" value={form.currency} onChange={e => set('currency', e.target.value)}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c} {currencySymbol(c)}</option>)}
          </select>
          {form.currency !== (po.currency || 'GBP').toUpperCase() && (
            <div className="mt-1 text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Changing the currency relabels the existing line amounts, it does not convert them. Check every line price is correct in {form.currency}.
            </div>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quote Reference <span className="font-normal text-gray-400">(supplier quote / quotation number)</span></label>
          <input className="form-input" placeholder="e.g. QT-2024-00123" value={form.quote_reference} onChange={e => set('quote_reference', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <textarea className="form-textarea" rows="2" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Specific PO Terms and Conditions</label>
          <textarea className="form-textarea" rows="3" value={form.specific_terms} onChange={e => set('specific_terms', e.target.value)} />
        </div>
      </div>
      {error && <div className="mt-2 text-[9px] text-red-600 font-semibold">{error}</div>}
    </Modal>
  );
}

function DeliveryAddressModal({ onClose }) {
  const [addresses, setAddresses] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', address_line1: '', address_line2: '', city: '', county: '', postcode: '', contact_name: '', is_default: false });

  const load = () => api.get('delivery-addresses', { all: '1' }).then(setAddresses);
  useEffect(() => { load(); }, []);

  const resetForm = () => setForm({ name: '', address_line1: '', address_line2: '', city: '', county: '', postcode: '', contact_name: '', is_default: false });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (editId) {
      await api.put(`delivery-addresses/${editId}`, form);
      setEditId(null);
    } else {
      await api.post('delivery-addresses', form);
      setShowAdd(false);
    }
    resetForm();
    load();
  };

  const deactivate = async (id) => {
    if (!confirm('Remove this delivery address?')) return;
    await api.delete(`delivery-addresses/${id}`);
    load();
  };

  const startEdit = (a) => {
    setForm({ name: a.name, address_line1: a.address_line1, address_line2: a.address_line2 || '', city: a.city || '', county: a.county || '', postcode: a.postcode || '', contact_name: a.contact_name || '', is_default: !!a.is_default });
    setEditId(a.id);
    setShowAdd(true);
  };

  return (
    <Modal
      title="Manage Delivery Addresses"
      onClose={onClose}
      wide
      footer={<button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>}
    >
      <div className="flex justify-end mb-2">
        <button onClick={() => { resetForm(); setEditId(null); setShowAdd(!showAdd); }} className="px-2 py-1 text-[8px] font-semibold bg-navy text-white rounded">
          {showAdd ? 'Cancel' : '+ Add Address'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
          <div className="text-[9px] font-bold text-gray-600 mb-2">{editId ? 'Edit Address' : 'New Address'}</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name / Label *</label>
              <input className="form-input" placeholder="e.g. RRSL - Raynesway" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address Line 1 *</label>
              <input className="form-input" value={form.address_line1} onChange={e => set('address_line1', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address Line 2</label>
              <input className="form-input" value={form.address_line2} onChange={e => set('address_line2', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">City</label>
              <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">County</label>
              <input className="form-input" value={form.county} onChange={e => set('county', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Postcode</label>
              <input className="form-input" value={form.postcode} onChange={e => set('postcode', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">FAO / Contact Name</label>
              <input className="form-input" placeholder="e.g. IT Stores" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="is-default" checked={!!form.is_default} onChange={e => set('is_default', e.target.checked)} />
              <label htmlFor="is-default" className="text-[9px] text-gray-600">Set as default delivery address</label>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <button
              onClick={save}
              disabled={!form.name || !form.address_line1}
              className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-40"
            >{editId ? 'Save Changes' : 'Add Address'}</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto"><table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Name</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Address</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Contact</th>
            <th className="px-2 py-1"></th>
          </tr>
        </thead>
        <tbody>
          {addresses.filter(a => a.is_active).map(a => (
            <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-2 py-1 text-[9px] font-semibold">
                {a.name}
                {a.is_default ? <span className="ml-1 text-[7px] bg-gold/20 text-gold px-1 rounded font-bold">DEFAULT</span> : null}
              </td>
              <td className="px-2 py-1 text-[9px] text-gray-500">{[a.address_line1, a.city, a.postcode].filter(Boolean).join(', ')}</td>
              <td className="px-2 py-1 text-[9px] text-gray-500">{a.contact_name || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">
                <button onClick={() => startEdit(a)} className="text-[8px] text-gold hover:underline mr-2">Edit</button>
                {a.is_active && !a.is_default && (
                  <button onClick={() => deactivate(a.id)} className="text-[8px] text-red-400 hover:text-red-600">Remove</button>
                )}
              </td>
            </tr>
          ))}
          {addresses.filter(a => a.is_active).length === 0 && <tr><td colSpan="4" className="text-center py-4 text-[9px] text-gray-400">No delivery addresses</td></tr>}
        </tbody>
      </table></div>
    </Modal>
  );
}
